import {
  MouseEvent,
  PointerEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback
} from "react";
import { Button, Input, Popconfirm } from "antd";
import dayjs from "utils/dayjs-config";
import type { Dayjs } from "dayjs";
import { DatePicker } from "components/antd-date-picker";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import { truncate } from "pages/schedule/schedule-page/compare-schedule-3/optimized-print-view/viz/truncate";
import {
  AxisTick,
  BidPackageRowGeometry,
  Geometry,
  MilestoneNode
} from "./model";
import { RenderConfig } from "./render-config";
import {
  placementForDate,
  type AddMilestonePlacement
} from "pages/planning-workspace/design-package-milestone-update";
import PlannerLaneBody from "pages/planning-workspace/components/timeline-view/planner-lane-body";

// Reproduces the original View Dependencies look (muted palette, gray timeline
// bars, orange dependency markers, bordered activity/duration boxes) but driven
// dynamically: real milestone names/dates and one band per bid package.

function fmtDate(d: string | null): string {
  return d ? DateUtils.format(d, DATE_FORMAT_MMDDYYYY) : "";
}

// Text that carries a <title> tooltip uses the hand cursor (not the default
// SVG text/I-beam cursor) to signal it is hoverable.
const TIP_CURSOR = { cursor: "pointer" } as const;

// "YYYY-MM-DD" ± n days, in UTC day arithmetic so no timezone can shift the
// calendar day. Used to build the no-cross bounds around a dragged milestone.
function addDaysIso(iso: string, n: number): string {
  const t =
    Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) +
    n * 86_400_000;
  const d = new Date(t);
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${m}-${day}`;
}

// The persisted dates are lunch-time (13:23 UTC) instants precisely so the
// calendar day reads the same in every timezone — the ISO prefix IS the day.
function dayOf(date: string): string {
  return date.slice(0, 10);
}

// Characters that fit in a pixel width at a font size. Conservative for Inter
// (~0.6em average) so text never overflows its box.
function fitChars(px: number, fontSize: number): number {
  return Math.max(4, Math.floor(px / (fontSize * 0.6)));
}

// Greedy word-wrap into at most `maxLines` lines of `maxChars`. Only the last
// line is ellipsised, and only if the text genuinely overflows all the lines.
function wrapText(text: string, maxChars: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  words.forEach((w) => {
    const next = cur ? `${cur} ${w}` : w;
    if (!cur || next.length <= maxChars) {
      cur = next;
    } else {
      lines.push(cur);
      cur = w;
    }
  });
  if (cur) lines.push(cur);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = truncate(lines.slice(maxLines - 1).join(" "), maxChars);
  return kept;
}

// Inlined CIQ entity icons (paths copied from src/components/svg-icons), scaled
// to `size` px and tinted with a design token so they render inside this SVG.
const ENTITY_ICONS = {
  designPackage: {
    vb: 17,
    d: "M16.151 6.97256C16.7718 6.97256 17.0309 6.17501 16.5267 5.80738L8.85458 0.226576C8.74501 0.146303 8.61272 0.103027 8.4769 0.103027C8.34107 0.103027 8.20878 0.146303 8.09922 0.226576L0.42712 5.80738C-0.0771216 6.173 0.18203 6.97256 0.804798 6.97256H2.04832V14.486H0.601896C0.513503 14.486 0.441182 14.5583 0.441182 14.6467V15.6913C0.441182 15.7797 0.513503 15.852 0.601896 15.852H16.3519C16.4403 15.852 16.5126 15.7797 16.5126 15.6913V14.6467C16.5126 14.5583 16.4403 14.486 16.3519 14.486H14.9055V6.97256H16.151ZM8.4769 1.64287L13.9231 5.60448H3.03069L8.4769 1.64287ZM3.49475 6.97256H5.8452V14.486H3.49475V6.97256ZM7.29163 6.97256H9.64208V14.486H7.29163V6.97256ZM13.459 14.486H11.0885V6.97256H13.459V14.486Z"
  },
  bidPackage: {
    vb: 18,
    d: "M9 0C4.02991 0 0 4.02991 0 9C0 13.9701 4.02991 18 9 18C13.9701 18 18 13.9701 18 9C18 4.02991 13.9701 0 9 0ZM9 16.4732C4.87366 16.4732 1.52679 13.1263 1.52679 9C1.52679 4.87366 4.87366 1.52679 9 1.52679C13.1263 1.52679 16.4732 4.87366 16.4732 9C16.4732 13.1263 13.1263 16.4732 9 16.4732ZM9.95826 8.53393L9.44799 8.4154V5.71741C10.2114 5.82188 10.6835 6.3 10.7638 6.88661C10.7739 6.96696 10.8422 7.02522 10.9225 7.02522H11.8246C11.919 7.02522 11.9933 6.94286 11.9853 6.84844C11.8627 5.59687 10.8321 4.7933 9.45603 4.65469V3.99777C9.45603 3.90937 9.38371 3.83705 9.29531 3.83705H8.7308C8.64241 3.83705 8.57009 3.90937 8.57009 3.99777V4.66071C7.14777 4.79933 6.03482 5.58482 6.03482 7.05134C6.03482 8.40938 7.03527 9.06429 8.08594 9.3154L8.58214 9.44196V12.3087C7.6942 12.1902 7.19598 11.7161 7.09353 11.0772C7.08147 11.0009 7.01317 10.9446 6.93482 10.9446H6.0067C5.91228 10.9446 5.83795 11.025 5.84598 11.1194C5.93638 12.2243 6.77411 13.2408 8.56205 13.3714V14.0022C8.56205 14.0906 8.63438 14.1629 8.72277 14.1629H9.2933C9.3817 14.1629 9.45402 14.0906 9.45402 14.0002L9.45 13.3634C11.023 13.2248 12.148 12.383 12.148 10.8723C12.146 9.47812 11.26 8.85536 9.95826 8.53393ZM8.58013 8.20848C8.46763 8.17634 8.37321 8.1462 8.27879 8.10804C7.59978 7.86295 7.28438 7.46719 7.28438 6.95692C7.28438 6.22768 7.83683 5.81183 8.58013 5.71741V8.20848ZM9.44799 12.3147V9.62879C9.51027 9.64687 9.56652 9.66094 9.62478 9.67299C10.575 9.96228 10.8944 10.3641 10.8944 10.9808C10.8944 11.7663 10.3038 12.2384 9.44799 12.3147Z"
  },
  // chain-link — the same icon submittal/material linking use (svg-icons/link-icon)
  link: {
    vb: 20,
    d: "M14.7143 12.3568L13.5357 11.1783L14.7143 9.99983C16.0161 8.69808 16.0161 6.58755 14.7143 5.2858C13.4126 3.98405 11.302 3.98405 10.0002 5.2858L8.82175 6.46431L7.64323 5.2858L8.82175 4.10729C10.7743 2.15467 13.9402 2.15467 15.8928 4.10729C17.8454 6.05991 17.8454 9.22575 15.8928 11.1783L14.7143 12.3568ZM12.3572 14.7139L11.1787 15.8924C9.22616 17.845 6.06031 17.845 4.10769 15.8924C2.15507 13.9397 2.15507 10.7739 4.10769 8.82133L5.28621 7.64282L6.46471 8.82133L5.28621 9.99983C3.98446 11.3016 3.98446 13.4122 5.28621 14.7139C6.58796 16.0157 8.6985 16.0157 10.0002 14.7139L11.1787 13.5354L12.3572 14.7139ZM12.3572 6.46431L13.5357 7.64282L7.64323 13.5354L6.46471 12.3568L12.3572 6.46431Z"
  }
} as const;

function EntityIcon({
  type,
  x,
  y,
  size,
  color
}: {
  type: keyof typeof ENTITY_ICONS;
  x: number;
  y: number;
  size: number;
  color: string;
}) {
  const icon = ENTITY_ICONS[type];
  const scale = size / icon.vb;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <path d={icon.d} fill={color} />
    </g>
  );
}

// Timeline x's start after the label column. The scale already produces
// intrinsic positions (fixed slot per date), so this is a simple offset and the
// SVG grows as wide as the content needs.
function makeScaleX(config: RenderConfig) {
  return (x: number) => config.labelColumnWidth + x;
}

// Small horizontal zigzag that breaks a line at (x, y) to mark a time leap.
function hZigzag(x: number, y: number): string {
  return `M ${x - 8} ${y} L ${x - 4} ${y - 4} L ${x} ${y + 4} L ${x + 4} ${y - 4} L ${x + 8} ${y}`;
}

// Rect path with only the RIGHT corners rounded. With closed=false the left edge
// is omitted, so stroking it draws top/right/bottom only (no left border) — lets
// the bid bar join its solid start cap seamlessly.
function rightRoundedRect(
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  closed = true
): string {
  const rr = Math.max(0, Math.min(r, w, h / 2));
  const base =
    `M${x} ${y} H${x + w - rr} A${rr} ${rr} 0 0 1 ${x + w} ${y + rr} ` +
    `V${y + h - rr} A${rr} ${rr} 0 0 1 ${x + w - rr} ${y + h} H${x}`;
  return closed ? `${base} Z` : base;
}

// Human-friendly gap label for the break tooltip.
function formatGap(days: number): string {
  if (days < 45) return `${days} days`;
  const months = Math.round(days / 30.44);
  if (months < 18) return `~${months} months`;
  const years = Math.floor(days / 365.25);
  const remMonths = Math.round((days - years * 365.25) / 30.44);
  return remMonths > 0 ? `~${years} yr ${remMonths} mo` : `~${years} yr`;
}

interface ScreenBreak {
  x: number;
  days: number;
  side: "left" | "right";
}

// Renders a horizontal line interrupted by a zigzag break wherever a break
// falls within (x1, x2). The zigzag carries a tooltip with the time leapt.
function LineWithBreaks({
  x1,
  x2,
  y,
  breaks,
  stroke,
  strokeWidth,
  markerEnd,
  config
}: {
  x1: number;
  x2: number;
  y: number;
  breaks: ScreenBreak[];
  stroke: string;
  strokeWidth: number;
  markerEnd?: string;
  config: RenderConfig;
}) {
  const pts = breaks
    .filter((b) => b.x > x1 + 12 && b.x < x2 - 12)
    .sort((a, b) => a.x - b.x);
  const segs: Array<[number, number]> = [];
  let cur = x1;
  pts.forEach((b) => {
    segs.push([cur, b.x - 9]);
    cur = b.x + 9;
  });
  segs.push([cur, x2]);

  return (
    <g>
      {segs.map(([a, c], i) => (
        <line
          key={`s-${a}-${c}`}
          x1={a}
          y1={y}
          x2={c}
          y2={y}
          stroke={stroke}
          strokeWidth={strokeWidth}
          markerEnd={i === segs.length - 1 ? markerEnd : undefined}
        />
      ))}
      {pts.map((b) => (
        <g key={`z-${b.x}`}>
          <path
            d={hZigzag(b.x, y)}
            stroke={config.palette.tick}
            strokeWidth={1.25}
            fill="none"
          />
          {/* wider transparent hit target so the tooltip is easy to trigger */}
          <rect
            x={b.x - 12}
            y={y - 12}
            width={24}
            height={24}
            fill="transparent"
            pointerEvents="all"
            style={{ cursor: "pointer" }}
          >
            <title>{`Time gap: ${formatGap(b.days)} (${b.days} days)`}</title>
          </rect>
        </g>
      ))}
    </g>
  );
}

interface PlacedMilestone {
  milestone: MilestoneNode;
  x: number; // screen x
  row: 0 | 1; // 0 = upper label row, 1 = lower
  name: string; // display name (possibly truncated with an ellipsis)
  isLinkTarget: boolean; // a bid package links into this milestone
}

const MILESTONE_MAX_W = 190; // labels wider than this get an ellipsis
const DESIGN_BAR_Y = 86; // top of the (tall) design-package track (incl. 24 top pad)
const TIMELINE_TOP = 114; // top of the timeline row (separator); diamonds sit here
const DIAMOND_CY = TIMELINE_TOP; // diamonds at the top edge of the timeline row

// When a milestone's committed date changes, its marker slides to the new x
// rather than snapping — a CSS transition on the group's `transform`. The
// marker is positioned by the transform attribute (user-unit exact under the
// SVG's viewBox), so at rest it renders pixel-identically to a hard-coded x;
// only an x change animates. Browsers without transitions just snap (today's
// behaviour), so this is purely additive.
const MS_MOVE_TRANSITION = "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)";

// Bar-end link handle — slightly larger than the milestone diamond (r=5) so it
// still reads clearly as its own, draggable, mark.
const LINK_HANDLE_R = 6;

// Milestone drag: the last EDGE_ZONE px at either end of the scale act as an
// auto-extend zone — holding the drag there keeps advancing the date over time
// (deeper = faster), so a drag can reach dates far beyond the drawn domain.
const EDGE_ZONE = 48;
const EDGE_TICK_MS = 120;

// The no-cross clamp both the pointer path and the edge-extend ticks apply:
// candidate day squeezed into [min, max]; inverted bounds (pre-existing
// out-of-order dates around the milestone) leave no legal day, so the drag
// stays on the original. `atBound` reports that the candidate was refused.
function clampToBounds(
  candidate: string,
  min: string | null,
  max: string | null,
  original: string
): { date: string; atBound: boolean } {
  let date = candidate;
  let atBound = false;
  if (min && date < min) {
    date = min;
    atBound = true;
  }
  if (max && date > max) {
    date = max;
    atBound = true;
  }
  if (min && max && min > max) {
    date = original;
    atBound = true;
  }
  return { date, atBound };
}

// Staggers design milestones into two rows by chronological order — even into
// the upper row, odd into the lower — so adjacent labels never sit at the same
// height. Names are capped with an ellipsis past a width cap.
function layoutMilestones(
  ticks: AxisTick[],
  scaleX: (x: number) => number,
  config: RenderConfig
): PlacedMilestone[] {
  const charW = config.fonts.name * 0.58;
  const maxChars = Math.max(6, Math.floor(MILESTONE_MAX_W / charW));
  return ticks
    .map((t) => ({
      milestone: t.milestone,
      x: scaleX(t.x),
      isLinkTarget: t.isLinkTarget
    }))
    .sort((a, b) => a.x - b.x)
    .map(({ milestone, x, isLinkTarget }, i) => ({
      milestone,
      x,
      row: (i % 2 === 0 ? 0 : 1) as 0 | 1,
      name: truncate(milestone.name, maxChars),
      isLinkTarget
    }));
}

type ScopeHoverInfo = {
  // Exactly one of these identifies the diamond being hovered, so the wrapper
  // can open the (unpinned) actions panel — the same panel a click pins.
  milestoneId?: string;
  bidPackageId?: string;
  label: string;
  items: string[];
  footer: string;
  clientX: number;
  clientY: number;
};

function MilestoneMarker({
  placed,
  config,
  onMilestoneDown,
  hovered,
  onHoverChange,
  scopeItems,
  onActivate,
  onScopeHover,
  onNameClick
}: {
  placed: PlacedMilestone;
  config: RenderConfig;
  onMilestoneDown?: (
    milestone: MilestoneNode,
    e: PointerEvent<SVGCircleElement>
  ) => void;
  /** True when this marker is the hovered one — it re-renders emphasized (and
   *  the HeaderBand paints it last) so it pops above near-overlapping
   *  neighbours. */
  hovered?: boolean;
  /** Mounted (with the oversized hover target) only for interactive
   *  consumers; the details page passes neither and renders as before. */
  onHoverChange?: (milestoneId: string | null) => void;
  /** Opt-in scope-item preview labels for this milestone — surfaced in the
   *  diamond's hover tooltip. Paired with onActivate; both absent on the
   *  details page, so it renders unchanged. */
  scopeItems?: string[];
  /** Opt-in: clicking the diamond opens its actions popover (change date +
   *  view/add scope). Carries the pointer position so the wrapper can anchor
   *  the popover to the diamond. */
  onActivate?: (m: {
    milestoneId: string;
    label: string;
    clientX: number;
    clientY: number;
  }) => void;
  /** Opt-in: reports hover enter/leave (with pointer position) so the wrapper
   *  can float a rich HTML tooltip previewing this milestone's scope items. */
  onScopeHover?: (info: ScopeHoverInfo | null) => void;
  /** Opt-in inline-rename of the milestone name. Off by default. */
  onNameClick?: (
    milestoneId: string,
    currentName: string,
    rect: DOMRect
  ) => void;
}) {
  const { palette, fonts } = config;
  const { milestone, x, row, name, isLinkTarget } = placed;
  // Reschedule-by-drag is offered only for milestones that can still move:
  // an actual date means the milestone has happened (the date block locks its
  // committed date too), and an undated milestone has no diamond to grab.
  const draggable = Boolean(
    onMilestoneDown && milestone.effectiveDate && !milestone.actualDate
  );
  const interactive = Boolean(onHoverChange);
  const nameY = row === 0 ? 38 : 68; // staggered names (incl. 24 top pad)
  const dateY = nameY + 14;
  const cy = DIAMOND_CY; // diamond centre on the track
  const r = hovered ? 8 : 5;
  // Emphasized (hovered) text paints a surface-coloured halo under itself
  // (paint-order: stroke) so it stays legible over a neighbour it overlaps.
  const halo = hovered
    ? ({
        stroke: palette.surface,
        strokeWidth: 4,
        paintOrder: "stroke"
      } as const)
    : undefined;
  // The diamond doubles as the milestone's entry point when a consumer opts
  // in: hover previews its scope, click opens the actions popover.
  const activatable = Boolean(onActivate);
  const scopePreview = scopeItems ?? [];
  const baseHint = draggable
    ? "Drag to change this milestone's date."
    : `${milestone.name} — completed (actual date), so its date is locked.`;
  const scopeHint = scopePreview.length
    ? `Design scope (${scopePreview.length}) — click to view or add:\n${scopePreview
        .map((s) => `• ${s}`)
        .join("\n")}`
    : "No scope items yet — click to add";
  const hoverTitle = activatable
    ? draggable
      ? `${baseHint}\n\n${scopeHint}`
      : scopeHint
    : baseHint;
  function milestoneMarkerGraphic() {
    function markerExtras() {
      return (
        <>
          {isLinkTarget && (
            <circle
              cx={0}
              cy={cy}
              r={r + 4}
              fill="none"
              stroke={palette.designPackage}
              strokeOpacity={0.5}
              strokeWidth={1.5}
            />
          )}
          {draggable && !isLinkTarget && !hovered && (
            <circle
              data-testid={`milestone-drag-affordance-${milestone.id}`}
              cx={0}
              cy={cy}
              r={r + 4}
              fill="none"
              stroke={palette.designPackage}
              strokeOpacity={0.45}
              strokeWidth={1.2}
              strokeDasharray="2.5 2.5"
            />
          )}
          {hovered && (
            <circle
              data-testid={`milestone-hover-ring-${milestone.id}`}
              cx={0}
              cy={cy}
              r={r + 5}
              fill="none"
              stroke={palette.designPackage}
              strokeOpacity={0.7}
              strokeWidth={1.5}
            />
          )}
          <path
            d={`M0 ${cy - r}L${r} ${cy}L0 ${cy + r}L${-r} ${cy}Z`}
            fill={palette.designPackage}
            stroke={palette.surface}
            strokeWidth={1}
          />
          {(draggable || interactive || activatable) && (
            <circle
              data-testid={
                draggable
                  ? `milestone-drag-handle-${milestone.id}`
                  : `milestone-hover-target-${milestone.id}`
              }
              cx={0}
              cy={cy}
              r={r + 6}
              fill="transparent"
              style={
                draggable
                  ? { cursor: "grab", touchAction: "none" }
                  : { cursor: activatable ? "pointer" : "default" }
              }
              onPointerDown={
                draggable ? (e) => onMilestoneDown?.(milestone, e) : undefined
              }
              onClick={
                !draggable && activatable
                  ? (e) =>
                      onActivate?.({
                        milestoneId: milestone.id,
                        label: milestone.name,
                        clientX: e.clientX,
                        clientY: e.clientY
                      })
                  : undefined
              }
              onMouseEnter={(e) => {
                onHoverChange?.(milestone.id);
                if (activatable)
                  onScopeHover?.({
                    milestoneId: milestone.id,
                    label: milestone.name,
                    items: scopePreview,
                    footer: draggable
                      ? "Click to change date or view scope · or drag"
                      : "Click to view scope items",
                    clientX: e.clientX,
                    clientY: e.clientY
                  });
              }}
              onMouseLeave={() => {
                onHoverChange?.(null);
                if (activatable) onScopeHover?.(null);
              }}
            >
              {!activatable && <title>{hoverTitle}</title>}
            </circle>
          )}
        </>
      );
    }
    return (
      // Positioned by transform (not per-child x) so a date change slides the
      // whole marker to its new x via MS_MOVE_TRANSITION. Children draw at the
      // local origin; the translate places them.
      <g
        transform={`translate(${x} 0)`}
        style={{ transition: MS_MOVE_TRANSITION }}
      >
        {/* connector from the label down to the milestone diamond */}
        <line
          x1={0}
          y1={dateY + 4}
          x2={0}
          y2={cy}
          stroke={hovered ? palette.designPackage : palette.tick}
          strokeWidth={1}
          strokeOpacity={hovered ? 0.9 : isLinkTarget ? 0.85 : 0.5}
        />
        {onNameClick && (
          <rect
            x={-(Math.max(48, name.length * fonts.name * 0.6) / 2)}
            y={nameY - fonts.name}
            width={Math.max(48, name.length * fonts.name * 0.6)}
            height={fonts.name + 8}
            fill="transparent"
            style={{ cursor: "pointer" }}
            data-testid={`ms-name-hit-${milestone.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onNameClick(
                milestone.id,
                milestone.name,
                e.currentTarget.getBoundingClientRect()
              );
            }}
          />
        )}
        <text
          x={0}
          y={nameY}
          textAnchor="middle"
          fontFamily={fonts.family}
          fontSize={fonts.name}
          fontWeight={hovered ? 700 : isLinkTarget ? 600 : 400}
          fill={hovered ? palette.designPackage : palette.text}
          style={{
            ...TIP_CURSOR,
            cursor: onNameClick ? "pointer" : TIP_CURSOR.cursor
          }}
          data-testid={onNameClick ? `ms-name-${milestone.id}` : undefined}
          onClick={
            onNameClick
              ? (e) => {
                  e.stopPropagation();
                  onNameClick(
                    milestone.id,
                    milestone.name,
                    e.currentTarget.getBoundingClientRect()
                  );
                }
              : undefined
          }
          {...halo}
        >
          <title>
            {onNameClick
              ? `${milestone.name} — click to rename`
              : milestone.name}
          </title>
          {name}
        </text>
        <text
          x={0}
          y={dateY}
          textAnchor="middle"
          fontFamily={fonts.family}
          fontSize={fonts.body}
          fontWeight={hovered ? 700 : 500}
          fill={hovered ? palette.text : palette.textMuted}
          {...halo}
        >
          {fmtDate(milestone.effectiveDate)}
        </text>
        {markerExtras()}
      </g>
    );
  }
  return milestoneMarkerGraphic();
}

// Faint vertical month/quarter gridlines through the scrolling rows.
function Gridlines({
  gridlines,
  scaleX,
  height,
  config
}: {
  gridlines: Geometry["gridlines"];
  scaleX: (x: number) => number;
  height: number;
  config: RenderConfig;
}) {
  if (!gridlines.length) return null;
  const { palette } = config;
  return (
    <g>
      {gridlines.map((gl) => {
        const gx = scaleX(gl.x);
        return (
          <line
            key={`grid-${gl.label}-${gl.x}`}
            x1={gx}
            y1={0}
            x2={gx}
            y2={height}
            stroke={palette.gridline}
            strokeWidth={1}
            strokeOpacity={0.4}
          />
        );
      })}
    </g>
  );
}

// The timeline row's background — a translucent band the (taller) design track
// overlaps, with a top separator and the side label. Drawn before the track.
function TimelineBand({
  geometry,
  config
}: {
  geometry: Geometry;
  config: RenderConfig;
}) {
  const { palette, fonts } = config;
  const bottom = geometry.axisHeight;
  return (
    <g>
      <rect
        x={0}
        y={TIMELINE_TOP}
        width="100%"
        height={bottom - TIMELINE_TOP}
        fill={palette.outOfScope}
        fillOpacity={0.5}
      />
      <line
        x1={0}
        y1={TIMELINE_TOP}
        x2="100%"
        y2={TIMELINE_TOP}
        stroke={palette.divider}
        strokeWidth={1}
      />
      <text
        x={24}
        y={bottom - 10}
        fontFamily={fonts.family}
        fontSize={fonts.caption}
        fontWeight={500}
        fill={palette.textMuted}
      >
        Timeline
      </text>
    </g>
  );
}

// The month scale — ticks anchored at the bottom of the timeline row, rising up
// (month > mid-month > week), with month labels in between. Drawn on top of the
// design track so it stays legible.
function TimelineScale({
  geometry,
  scaleX,
  config
}: {
  geometry: Geometry;
  scaleX: (x: number) => number;
  config: RenderConfig;
}) {
  const { palette, fonts } = config;
  const { gridlines, weekTicks } = geometry;
  if (!gridlines.length) return null;
  const bottom = geometry.axisHeight;
  const labelY = bottom - 10;

  // Mid-month ticks: midpoint between consecutive month markers (≈ the 15th).
  const monthXs = gridlines.map((gl) => scaleX(gl.x));
  const midXs: number[] = [];
  for (let i = 0; i < monthXs.length - 1; i += 1) {
    midXs.push((monthXs[i] + monthXs[i + 1]) / 2);
  }

  return (
    <g>
      {/* week ticks — only for short spans (a finer, proper scale) */}
      {weekTicks.map((wx) => {
        const x = scaleX(wx);
        return (
          <line
            key={`week-${Math.round(x)}`}
            x1={x}
            y1={bottom - 3}
            x2={x}
            y2={bottom}
            stroke={palette.tick}
            strokeWidth={1}
            strokeOpacity={0.3}
          />
        );
      })}
      {/* mid-month ticks (medium) */}
      {midXs.map((x) => (
        <line
          key={`mid-${Math.round(x)}`}
          x1={x}
          y1={bottom - 5}
          x2={x}
          y2={bottom}
          stroke={palette.tick}
          strokeWidth={1}
          strokeOpacity={0.5}
        />
      ))}
      {/* month ticks + labels */}
      {gridlines.map((gl) => {
        const gx = scaleX(gl.x);
        return (
          <g key={`ruler-${gl.label}-${gl.x}`}>
            <line
              x1={gx}
              y1={bottom - 8}
              x2={gx}
              y2={bottom}
              stroke={palette.tick}
              strokeWidth={1}
              strokeOpacity={0.7}
            />
            <text
              x={gx}
              y={labelY}
              textAnchor="middle"
              fontFamily={fonts.family}
              fontSize={fonts.caption}
              fill={palette.textMuted}
            >
              {gl.label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function HeaderBand({
  geometry,
  scaleX,
  config,
  onMilestoneDown,
  milestoneScopeItems,
  onMilestoneActivate,
  onMilestoneScopeHover,
  onMilestoneNameClick
}: {
  geometry: Geometry;
  scaleX: (x: number) => number;
  config: RenderConfig;
  onMilestoneDown?: (
    milestone: MilestoneNode,
    e: PointerEvent<SVGCircleElement>
  ) => void;
  milestoneScopeItems?: Record<string, string[]>;
  onMilestoneActivate?: (m: {
    milestoneId: string;
    label: string;
    clientX: number;
    clientY: number;
  }) => void;
  onMilestoneScopeHover?: (info: ScopeHoverInfo | null) => void;
  onMilestoneNameClick?: (
    milestoneId: string,
    currentName: string,
    rect: DOMRect
  ) => void;
}) {
  const { palette, fonts } = config;
  const placed = layoutMilestones(geometry.axis.ticks, scaleX, config);
  const xs = placed.map((p) => p.x);
  const trackX = scaleX(geometry.inScope.start);
  const trackW = Math.max(scaleX(geometry.inScope.end) - trackX, 1);
  // vertically centre the "Design Package Schedule" label in the band
  const labelY = Math.round((geometry.axisHeight - 15) / 2) + 11;

  // Hover-identify (interactive consumers only): milestones a day apart draw
  // nearly on top of each other, so the hovered one re-renders LAST — SVG
  // paints in document order, so it lands above its overlapping neighbours.
  const [hoverId, setHoverId] = useState<string | null>(null);
  const interactive = Boolean(onMilestoneDown);
  const ordered = hoverId
    ? [
        ...placed.filter((p) => p.milestone.id !== hoverId),
        ...placed.filter((p) => p.milestone.id === hoverId)
      ]
    : placed;

  // Where the date scale ends and the activity/lead-time cluster begins — the
  // right edge of everything a drag can reach.
  const clusterX = geometry.width - config.clusterWidth;

  return (
    <g>
      {/* tall design timeline track — whisper-light wash so bars/markers sit
          clearly in front; border kept faint for the same reason */}
      {xs.length > 0 && (
        <rect
          x={trackX}
          y={DESIGN_BAR_Y}
          width={trackW}
          height={geometry.axisHeight - DESIGN_BAR_Y}
          rx={config.barRadius}
          fill={palette.designPackage}
          fillOpacity={0.1}
          stroke={palette.designPackage}
          strokeOpacity={0.22}
          strokeWidth={1}
        />
      )}
      {/* dashed continuations of the design track through the margins (only
          for interactive consumers): the scale doesn't stop at the first/last
          milestone — these show the surface a drag can still reach. */}
      {interactive && xs.length > 0 && (
        <g data-testid="track-extensions">
          <line
            x1={scaleX(0)}
            y1={DIAMOND_CY}
            x2={trackX}
            y2={DIAMOND_CY}
            stroke={palette.designPackage}
            strokeOpacity={0.35}
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
          <line
            x1={trackX + trackW}
            y1={DIAMOND_CY}
            x2={clusterX}
            y2={DIAMOND_CY}
            stroke={palette.designPackage}
            strokeOpacity={0.35}
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
        </g>
      )}
      {/* separator in front of the activity / lead-time column — the drag
          surface ends here. Interactive consumers only, so the details page
          renders unchanged. */}
      {interactive && (
        <line
          data-testid="cluster-separator"
          x1={clusterX}
          y1={0}
          x2={clusterX}
          y2={geometry.axisHeight}
          stroke={palette.divider}
          strokeWidth={1.5}
        />
      )}
      {ordered.map((p) => (
        <MilestoneMarker
          key={p.milestone.id}
          placed={p}
          config={config}
          onMilestoneDown={onMilestoneDown}
          hovered={interactive && p.milestone.id === hoverId}
          onHoverChange={interactive ? setHoverId : undefined}
          scopeItems={milestoneScopeItems?.[p.milestone.id]}
          onActivate={onMilestoneActivate}
          onScopeHover={onMilestoneScopeHover}
          onNameClick={onMilestoneNameClick}
        />
      ))}
      {/* left label */}
      <EntityIcon
        type="designPackage"
        x={24}
        y={labelY - 12}
        size={16}
        color={palette.designPackage}
      />
      <text
        x={48}
        y={labelY}
        fontFamily={fonts.family}
        fontSize={fonts.body}
        fontWeight={700}
        fill={palette.textMuted}
      >
        Design Package Schedule
      </text>
    </g>
  );
}

function DurationPill({
  row,
  cx,
  bandTop,
  config
}: {
  row: BidPackageRowGeometry["row"];
  cx: number;
  bandTop: number;
  config: RenderConfig;
}) {
  const { palette, fonts } = config;
  // Sits just above the activity box (not beside it) so the box can move left,
  // closer to the bar. +10 leaves breathing room above the cluster.
  const pillX = cx + 24;
  const pillY = bandTop + 10;
  const pillW = 66;
  const pillH = 18;

  // Native <title> tooltip — never clipped or hidden behind the sticky bands.
  const tip = row.durations.map((s) => `${s.label}: ${s.days} days`).join("\n");

  return (
    <g style={TIP_CURSOR}>
      <title>{tip}</title>
      <rect
        x={pillX}
        y={pillY}
        width={pillW}
        height={pillH}
        rx={2}
        fill={palette.surface}
        stroke={palette.boxBorder}
        strokeOpacity={0.2}
        strokeWidth={0.5}
      />
      <text
        x={pillX + 6}
        y={pillY + 13}
        fontFamily={fonts.family}
        fontSize={fonts.body}
        fill={palette.textMuted}
      >
        {`${row.totalDays} days`}
      </text>
    </g>
  );
}

function isSpaceOrHyphen(char: string): boolean {
  return char === "-" || char.trim() === "";
}

/** Drop a trailing "(DELETED)" marker without a backtracking regex. */
function withoutDeletedMarker(name: string): string {
  const trimmedRight = name.trimEnd();
  const marker = "(deleted)";
  if (!trimmedRight.toLowerCase().endsWith(marker)) {
    return name.trim() || name;
  }
  let cut = trimmedRight.length - marker.length;
  while (cut > 0 && isSpaceOrHyphen(trimmedRight[cut - 1])) cut -= 1;
  return trimmedRight.slice(0, cut).trim() || name;
}

function ActivityBox({
  row,
  cx,
  bandTop,
  config
}: {
  row: BidPackageRowGeometry["row"];
  cx: number;
  bandTop: number;
  config: RenderConfig;
}) {
  const { palette, fonts } = config;
  const gov = row.governing;
  if (!gov) return null;
  const boxX = cx + 24; // pulled left (gap reduced); fits within clusterWidth 240
  const boxY = bandTop + 30; // leaves room for the duration pill stacked above
  const boxW = 196;
  const boxH = 46;
  const textX = boxX + 30; // text starts after the activity icon
  const nameMax = fitChars(boxW - 38, fonts.body);
  const accent = gov.isDeleted ? palette.deleted : palette.text;

  // A manual "set date" governing (planning-only-activity) and the material
  // -deadline fallback both lack a real activity name, so they share the
  // generic "Required on Site (set date)" label. For a real activity, drop the
  // redundant "(DELETED)" marker (the removed cue is shown separately below).
  const displayName =
    gov.source === "MANUAL" || gov.source === "MATERIAL_DEADLINE"
      ? "Required on Site (set date)"
      : withoutDeletedMarker(gov.name);

  // Full tooltip — uses the display name (never the raw planning-only title,
  // which is the bid-package name) so the box never shows wrong text on hover.
  const tooltip =
    !gov.isDeleted && gov.startDate
      ? `${displayName} — ${fmtDate(gov.startDate)}`
      : displayName;

  // Per the activity-box spec: the linked activity + its date only (no material,
  // no bid-package name).
  return (
    <g style={TIP_CURSOR}>
      <title>{tooltip}</title>
      <rect
        x={boxX}
        y={boxY}
        width={boxW}
        height={boxH}
        rx={4}
        fill={palette.surface}
        stroke={palette.boxBorder}
        strokeOpacity={0.18}
        strokeWidth={1}
      />
      {/* mini gantt-bars glyph — reads as a schedule activity (not a notepad) */}
      <g transform={`translate(${boxX + 8} ${boxY + 9})`}>
        <rect x={0} y={0} width={12} height={2.5} rx={1.25} fill={accent} />
        <rect
          x={3.5}
          y={4.5}
          width={9.5}
          height={2.5}
          rx={1.25}
          fill={accent}
        />
        <rect x={1.5} y={9} width={7} height={2.5} rx={1.25} fill={accent} />
      </g>
      <text
        x={textX}
        y={boxY + 18}
        fontFamily={fonts.family}
        fontSize={fonts.body}
        fontWeight={600}
        fill={accent}
      >
        {truncate(displayName, nameMax)}
      </text>
      {/* governing (Required-on-Site) date, or the removed cue */}
      <text
        x={textX}
        y={boxY + 33}
        fontFamily={fonts.family}
        fontSize={fonts.caption}
        fontStyle={gov.isDeleted ? "italic" : "normal"}
        fill={gov.isDeleted ? palette.deleted : palette.textMuted}
      >
        {gov.isDeleted ? "(removed from schedule)" : fmtDate(gov.startDate)}
      </text>
    </g>
  );
}

// Dependency lines + connector arrows for one bid-package row, drawn in a back
// layer so the row's text always sits on top (the dashed line no longer paints
// over the labels).
function BidPackageDepLayer({
  g,
  scaleX,
  totalWidth,
  breaks,
  config,
  onLinkHandleDown
}: {
  g: BidPackageRowGeometry;
  scaleX: (x: number) => number;
  totalWidth: number;
  breaks: ScreenBreak[];
  config: RenderConfig;
  onLinkHandleDown?: (bpId: string, e: PointerEvent<SVGPathElement>) => void;
}) {
  const { palette } = config;
  const { row } = g;
  if (g.placeholder || !g.bar) return null;
  const bandTop = g.y;
  const tlY = bandTop + 25;
  const cx = totalWidth - config.clusterWidth;
  const x1 = scaleX(g.bar.x);
  const x2 = scaleX(g.bar.x + g.bar.width);
  const midY = tlY + config.smallTimelineHeight / 2;
  // Screen x of the linked design milestone (directly under its header diamond).
  const linkX =
    g.linkBadge && g.linkBadge.milestoneX != null
      ? scaleX(g.linkBadge.milestoneX)
      : null;

  return (
    <g>
      {/* orange dependency drop-line at the bid package's start — always shown;
          kept thin and semi-opaque so it reads as a connector, not a foreground mark */}
      <path
        d={`M${x1} ${config.axisHeight}V${tlY}`}
        stroke={palette.dependency}
        strokeWidth={1}
        strokeOpacity={0.65}
        strokeDasharray="5 3"
      />
      {/* dotted connector: drops from the linked design milestone (under its
          accented diamond) and elbows to this bid package's bar — showing
          exactly which milestone the package hangs off. Several of these cross
          when packages link to different milestones, which is expected. */}
      {linkX !== null && (
        <g>
          <path
            d={`M${linkX} ${config.axisHeight} V${midY} H${x1}`}
            fill="none"
            stroke={palette.designPackage}
            strokeWidth={1}
            strokeOpacity={0.65}
            strokeDasharray="5 3"
          />
          {/* wide transparent grab target over the whole connector so the link
              can be picked up anywhere along the line, then dragged to another
              milestone — reuses the same drag as the bar-start handle. */}
          {onLinkHandleDown && (
            <path
              data-testid={`bp-link-line-${row.id}`}
              d={`M${linkX} ${config.axisHeight} V${midY} H${x1}`}
              fill="none"
              stroke="transparent"
              strokeWidth={12}
              pointerEvents="stroke"
              style={{ cursor: "grab" }}
              onPointerDown={(e) => onLinkHandleDown(row.id, e)}
            />
          )}
          <circle
            cx={linkX}
            cy={config.axisHeight}
            r={2}
            fill={palette.designPackage}
            fillOpacity={0.65}
          />
        </g>
      )}

      {/* connector to the activity cluster, broken by a zigzag at a time-jump */}
      {row.governing && (
        <LineWithBreaks
          x1={x2}
          x2={cx + 24}
          y={tlY + config.smallTimelineHeight / 2}
          breaks={breaks}
          stroke={palette.arrow}
          strokeWidth={1}
          markerEnd="url(#dep-arrow)"
          config={config}
        />
      )}
    </g>
  );
}

// Row content: label + divider, bid-package bar, on-bar date/name, duration
// pill and activity box. Drawn on top of the dependency back layer.
// The bar-start link handle, in its own component so it can hold local hover
// state: on hover the diamond pops (grows) and its halo thickens, and a native
// <title> tooltip explains what a drag does — making the link affordance
// discoverable rather than a mystery mark.
function LinkHandle(props: {
  rowId: string;
  label?: string;
  scopeItems?: string[];
  x: number;
  y: number;
  palette: RenderConfig["palette"];
  onDown: (bpId: string, e: PointerEvent<SVGPathElement>) => void;
  /** Opt-in: previews the bid package's scope items in the shared hover
   *  tooltip. When present, the diamond also opens the scope modal on click
   *  (handled by the wrapper's link pointer-up). */
  onScopeHover?: (info: ScopeHoverInfo | null) => void;
}) {
  const { rowId, label, scopeItems, x, y, palette, onDown, onScopeHover } =
    props;
  const [hover, setHover] = useState(false);
  const r = hover ? LINK_HANDLE_R * 1.6 : LINK_HANDLE_R;
  const scopeInteractive = Boolean(onScopeHover);
  return (
    <path
      data-testid={`bp-link-handle-${rowId}`}
      d={`M${x} ${y - r}L${x + r} ${y}L${x} ${y + r}L${x - r} ${y}Z`}
      fill={palette.dependency}
      stroke={palette.surface}
      strokeWidth={hover ? 2 : 1}
      style={{ cursor: "grab" }}
      onPointerEnter={(e) => {
        setHover(true);
        if (scopeInteractive)
          onScopeHover?.({
            bidPackageId: rowId,
            label: label ?? "",
            items: scopeItems ?? [],
            footer: "Click to view scope items · or drag to link",
            clientX: e.clientX,
            clientY: e.clientY
          });
      }}
      onPointerLeave={() => {
        setHover(false);
        if (scopeInteractive) onScopeHover?.(null);
      }}
      onPointerDown={(e) => onDown(rowId, e)}
    >
      {/* The rich tooltip replaces the native one when scope is interactive. */}
      {!scopeInteractive && (
        <title>
          Drag onto a design milestone to link it, or grab the connection line
          to re-link.
        </title>
      )}
    </path>
  );
}

function BidPackageContent({
  g,
  scaleX,
  totalWidth,
  config,
  onNameClick,
  onLinkHandleDown,
  scopeItems,
  onScopeHover
}: {
  g: BidPackageRowGeometry;
  scaleX: (x: number) => number;
  totalWidth: number;
  config: RenderConfig;
  /** Opt-in inline-rename trigger — omitted entirely (no data-testid/onClick)
   *  when the caller doesn't want rename, so the default render is untouched. */
  onNameClick?: (bpId: string, currentTitle: string, rect: DOMRect) => void;
  /** Opt-in bar-end drag handle for linking/re-linking to a design milestone —
   *  omitted entirely (no handle rendered) when the caller doesn't want it. */
  onLinkHandleDown?: (bpId: string, e: PointerEvent<SVGPathElement>) => void;
  /** Opt-in scope preview + hover for this bid package's link diamond. */
  scopeItems?: string[];
  onScopeHover?: (info: ScopeHoverInfo | null) => void;
}) {
  const { palette, fonts } = config;
  const { row } = g;
  const bandTop = g.y;
  const cx = totalWidth - config.clusterWidth;

  // Left label + divider. The bid-package name wraps within the label column
  // (no truncation); the subtitle follows below it, and the whole block is
  // vertically centred in the row.
  const fullName = `${row.number} ${row.name}`.trim();
  const nameLines = wrapText(
    fullName,
    fitChars(config.labelColumnWidth - 56, fonts.name),
    5
  );
  const linkName = g.linkBadge?.milestoneName ?? null;
  // name lines + "Bid Package" subtitle (+ the "→ milestone" link caption)
  const lineCount = nameLines.length + 1 + (linkName ? 1 : 0);
  const firstLineY =
    bandTop + Math.round((config.rowHeight - lineCount * 15) / 2) + 11;
  const subtitleY = firstLineY + nameLines.length * 15 + 2;
  const captionY = subtitleY + 15;
  const label = (
    <>
      <line
        x1={0}
        y1={bandTop}
        x2="100%"
        y2={bandTop}
        stroke={palette.divider}
        strokeWidth={1}
      />
      <EntityIcon
        type="bidPackage"
        x={24}
        y={firstLineY - 12}
        size={16}
        color={palette.bidPackage}
      />
      <text
        x={48}
        y={firstLineY}
        fontFamily={fonts.family}
        fontSize={fonts.name}
        fontWeight={500}
        fill={palette.text}
        style={TIP_CURSOR}
        data-testid={onNameClick ? `bp-name-${row.id}` : undefined}
        onClick={
          onNameClick
            ? (e) =>
                onNameClick(
                  row.id,
                  row.name,
                  e.currentTarget.getBoundingClientRect()
                )
            : undefined
        }
      >
        <title>{fullName}</title>
        {nameLines.map((ln, i) => (
          <tspan key={`${ln}-${i}`} x={48} dy={i === 0 ? 0 : 15}>
            {ln}
          </tspan>
        ))}
      </text>
      <text
        x={48}
        y={subtitleY}
        fontFamily={fonts.family}
        fontSize={fonts.caption}
        fontWeight={400}
        fill={palette.textMuted}
      >
        Bid Package
      </text>
      {/* milestone-of-link caption — link-chain icon (same as submittal/material
          linking) + the milestone name; the print/grayscale-safe carrier that
          pairs with the accented design-milestone diamond of the same name */}
      {linkName && (
        <g style={TIP_CURSOR}>
          <title>{`Links to design milestone: ${linkName}`}</title>
          <EntityIcon
            type="link"
            x={48}
            y={captionY - 10}
            size={12}
            color={palette.designPackage}
          />
          <text
            x={63}
            y={captionY}
            fontFamily={fonts.family}
            fontSize={fonts.caption}
            fontWeight={600}
            fill={palette.designPackage}
          >
            {truncate(
              linkName,
              fitChars(config.labelColumnWidth - 71, fonts.caption)
            )}
          </text>
        </g>
      )}
    </>
  );

  if (g.placeholder || !g.bar) {
    return (
      <g>
        {label}
        <text
          x={config.labelColumnWidth + config.timelinePadding}
          y={bandTop + 48}
          fontFamily={fonts.family}
          fontSize={fonts.body}
          fontWeight={400}
          fill={palette.textMuted}
        >
          {config.noPlanText}
        </text>
      </g>
    );
  }

  const tlY = bandTop + 25;
  const x1 = scaleX(g.bar.x);
  const x2 = scaleX(g.bar.x + g.bar.width);
  const barCenterY = tlY + config.smallTimelineHeight / 2;
  const first = row.milestones[0];
  // latest milestone — its date is shown at the bar end when this bid package is
  // part of a time-jump (its plan sits outside the design span).
  const datedMs = row.milestones.filter((m) => m.effectiveDate);
  const lastMs = datedMs.reduce<MilestoneNode | null>(
    (latest, m) =>
      // datedMs is pre-filtered to a truthy effectiveDate (and `latest` is
      // always one of its members), so neither `?? ""` fallback can run
      /* v8 ignore next */
      latest && (latest.effectiveDate ?? "") > (m.effectiveDate ?? "")
        ? latest
        : m,
    null
  );

  return (
    <g>
      {label}

      {/* bid-package bar — right corners rounded; the left edge is open (no left
          border) so the solid start cap joins it as one shape */}
      <path
        d={rightRoundedRect(
          x1,
          tlY,
          Math.max(x2 - x1, 1),
          config.smallTimelineHeight,
          config.barRadius
        )}
        fill={palette.bidPackage}
        fillOpacity={0.5}
      />
      <path
        d={rightRoundedRect(
          x1,
          tlY,
          Math.max(x2 - x1, 1),
          config.smallTimelineHeight,
          config.barRadius,
          false
        )}
        fill="none"
        stroke={palette.bidPackage}
        strokeWidth={1}
      />
      {/* solid start cap — square on the right, flush with the bar's height */}
      <rect
        x={x1 - config.milestoneTickWidth / 2}
        y={tlY - 0.5}
        width={config.milestoneTickWidth}
        height={config.smallTimelineHeight + 1}
        fill={palette.bidPackage}
      />
      {/* bar-START link handle — the link attaches to the bid package's start
          (its milestone), so the handle sits there. LinkHandle pops + shows a
          hover tooltip to advertise the drag. */}
      {onLinkHandleDown && (
        <LinkHandle
          rowId={row.id}
          label={row.name}
          scopeItems={scopeItems}
          x={x1}
          y={barCenterY}
          palette={palette}
          onDown={onLinkHandleDown}
          onScopeHover={onScopeHover}
        />
      )}
      {first && (
        <>
          <text
            x={x1 + 8}
            y={bandTop + 51}
            fontFamily={fonts.family}
            fontSize={fonts.body}
            fontWeight={600}
            fill={palette.text}
          >
            {fmtDate(first.effectiveDate)}
          </text>
          <text
            x={x1 + 8}
            y={bandTop + 66}
            fontFamily={fonts.family}
            fontSize={fonts.body}
            fontWeight={400}
            fill={palette.textMuted}
            style={TIP_CURSOR}
          >
            <title>{first.name}</title>
            {truncate(first.name, 20)}
          </text>
        </>
      )}
      {/* time-jump rows: show the last milestone's date at the bar end so the
          compressed bar still conveys when the plan actually ends */}
      {row.outOfScope && lastMs && (
        <text
          x={x2 + 5}
          y={tlY - 4}
          fontFamily={fonts.family}
          fontSize={fonts.caption}
          fontWeight={600}
          fill={palette.text}
        >
          {fmtDate(lastMs.effectiveDate)}
        </text>
      )}

      <DurationPill row={row} cx={cx} bandTop={bandTop} config={config} />
      <ActivityBox row={row} cx={cx} bandTop={bandTop} config={config} />
    </g>
  );
}

// Sticky-footer legend explaining the marks. Plain HTML/Tailwind to match the
// app's lightweight legend pattern (e.g. governing-legend).
function LegendItem({
  swatch,
  label,
  config
}: {
  swatch: ReactNode;
  label: string;
  config: RenderConfig;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex items-center justify-center"
        style={{ width: 20, height: 14 }}
      >
        {swatch}
      </span>
      <span
        style={{
          fontSize: 11,
          fontFamily: config.fonts.family,
          color: config.palette.textMuted
        }}
      >
        {label}
      </span>
    </div>
  );
}

export function DependencyLegend({ config }: { config: RenderConfig }) {
  const { palette } = config;
  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-2 px-6 py-4"
      style={{
        background: palette.surface,
        borderTop: `1px solid ${palette.divider}`
      }}
    >
      <LegendItem
        config={config}
        label="Design milestone"
        swatch={
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 1,
              background: palette.designPackage,
              transform: "rotate(45deg)"
            }}
          />
        }
      />
      <LegendItem
        config={config}
        label="Links to milestone"
        swatch={
          <svg width={14} height={14} viewBox="0 0 20 20">
            <path
              d={ENTITY_ICONS.link.d}
              fill={palette.designPackage}
              fillOpacity={0.9}
            />
          </svg>
        }
      />
      <LegendItem
        config={config}
        label="Bid Package plan"
        swatch={
          <span
            style={{
              width: 18,
              height: 8,
              borderRadius: 2,
              background: palette.bidPackage,
              opacity: 0.5
            }}
          />
        }
      />
      <LegendItem
        config={config}
        label="Dependency"
        swatch={
          <span
            style={{
              width: 18,
              borderTop: `2px dashed ${palette.dependency}`,
              opacity: 0.8
            }}
          />
        }
      />
    </div>
  );
}

export default function DependencyTimeline(props: {
  geometry: Geometry;
  config: RenderConfig;
  /** Optional content rendered just above the legend — lets callers inject
   *  actions (e.g. "Add bid package") that are visually part of the timeline
   *  footer without coupling this component to any specific feature. */
  footerSlot?: ReactNode;
  /** Opt-in hover crosshair (vertical guide + date pill). Off by default so
   *  the details-page consumer renders unchanged; only the planner lane
   *  turns it on. */
  crosshair?: boolean;
  /** Opt-in inline-rename of a bid package's title from its name label. Off by
   *  default (details-page consumer renders unchanged); the planner lane wires
   *  this to a mutation. */
  onRenameBidPackage?: (bpId: string, nextTitle: string) => void;
  /** Opt-in inline-rename of a design-package milestone name. Off by default. */
  onRenameMilestone?: (milestoneId: string, nextName: string) => void;
  /** Opt-in delete of a design-package milestone from its actions popover.
   *  Off by default. A package must keep at least two named milestones. */
  onDeleteMilestone?: (
    milestoneId: string,
    options?: { moveToMilestoneId?: string }
  ) => void | Promise<void>;
  /** Opt-in: click the date scale to open "Add New Milestone". Off by default. */
  onAddMilestone?: (placement: AddMilestonePlacement) => void;
  /** Opt-in bar-end drag-to-link: dragging a bid package's end handle onto a
   *  design-milestone diamond links (or re-links) it to that milestone. Off by
   *  default (details-page consumer renders unchanged); the planner lane wires
   *  this to a mutation. */
  onLinkChange?: (
    bpId: string,
    milestoneId: string,
    info?: { caution?: string }
  ) => void;
  /** Opt-in drag-to-reschedule of a design milestone: dragging its diamond
   *  along the axis calls back with the day it was dropped on ("YYYY-MM-DD").
   *  The drag is clamped between the milestone's sequence neighbours so no two
   *  milestones can cross, and milestones with an actual date are locked. Off
   *  by default (details-page consumer renders unchanged). */
  onMilestoneReschedule?: (milestoneId: string, isoDate: string) => void;
  /** Opt-in edit of a design milestone's actual date from the planner hover
   *  card. `null` clears it. Off by default. */
  onMilestoneActualDate?: (milestoneId: string, isoDate: string | null) => void;
  /** Whether the milestone diamond can be DRAGGED to reschedule. Off by
   *  default: dragging hijacked clicks and nudged the timeline by a pixel or
   *  two, so dates change only via the click popover's date picker (still
   *  powered by `onMilestoneReschedule`). */
  milestoneDragEnabled?: boolean;
  /** Opt-in scope entry point on milestone diamonds: `milestoneScopeItems`
   *  (milestoneId → short preview labels) feeds the diamond's hover tooltip,
   *  and `onMilestoneScopeOpen` fires when the diamond is clicked (view + add).
   *  Off by default (details-page consumer renders unchanged). */
  milestoneScopeItems?: Record<string, string[]>;
  onMilestoneScopeOpen?: (m: { milestoneId: string; label: string }) => void;
  /** Opt-in scope entry point on bid-package diamonds: `bidScopeItems`
   *  (bidPackageId → preview labels) feeds the diamond's hover tooltip, and
   *  `onBidScopeOpen` fires when the diamond is clicked (view + add). No date
   *  editing — a bid package has no date of its own. Off by default. */
  bidScopeItems?: Record<string, string[]>;
  onBidScopeOpen?: (b: {
    bidPackageId: string;
    label: string;
    milestoneName?: string;
  }) => void;
  /** Planner card: the lane (milestone chart and bid rows) instead of the
   *  details-page SVG. Off by default. */
  plannerLayout?: boolean;
  /** Read-only print: keep the required-on-site column inside the page. */
  printMode?: boolean;
  /** Planner card: later bid package dates to scale, no right overflow zone. */
  fullTimeline?: boolean;
  projectId?: string;
  designPackageName?: string;
  /** Saves a required-on-site date when the bid package has none. */
  onSetRequiredOnSite?: (bidPackageId: string, isoDate: string | null) => void;
  /** Opens Manage Bid Packages from the empty planner heading. */
  onManageBidPackages?: () => void;
  /** Opens a bid package's details page ("Create plan" on its card). */
  onCreatePlan?: (bidPackageId: string) => void;
}) {
  const {
    geometry,
    config,
    footerSlot,
    crosshair,
    onRenameBidPackage,
    onRenameMilestone,
    onDeleteMilestone,
    onAddMilestone,
    onLinkChange,
    onMilestoneReschedule,
    onMilestoneActualDate,
    milestoneDragEnabled = false,
    milestoneScopeItems,
    onMilestoneScopeOpen,
    bidScopeItems,
    onBidScopeOpen,
    plannerLayout = false,
    printMode = false,
    fullTimeline = false,
    projectId,
    designPackageName,
    onSetRequiredOnSite,
    onManageBidPackages,
    onCreatePlan
  } = props;
  const { palette } = config;
  const scaleX = makeScaleX(config);
  const breakXs: ScreenBreak[] = geometry.breaks.map((b) => ({
    x: scaleX(b.x),
    days: b.days,
    side: b.side
  }));
  const bodyHeight = geometry.height - geometry.axisHeight;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Scroll container whose clientWidth is the SVG's painted width. A vertical
  // scrollbar shrinks clientWidth but not the wrapper's border-box — pointer
  // → geometry math must use the painted width or inverted dates shift left.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scopePopoverRef = useRef<HTMLDivElement | null>(null);
  const [cross, setCross] = useState<{ x: number; date: string } | null>(null);

  // cssX is wrapper-local (overlay positioning). geomX / scale use the scroll
  // content width so a classic-overlay scrollbar can't skew the date scale.
  const pointerToGeometry = (clientX: number) => {
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return null;
    const wrapperRect = wrapperEl.getBoundingClientRect();
    const cssX = clientX - wrapperRect.left;
    const scrollEl = scrollRef.current;
    const paintedWidth =
      scrollEl && scrollEl.clientWidth > 0
        ? scrollEl.clientWidth
        : wrapperRect.width;
    if (paintedWidth <= 0) return null;
    const scale = paintedWidth / geometry.width;
    return { cssX, geomX: cssX / scale, scale };
  };

  // The clicked text's own on-screen box (via getBoundingClientRect at click
  // time) sidesteps reimplementing the SVG's viewBox/scroll math to place the
  // HTML input — it's just translated into the wrapper's local coordinates.
  const [editing, setEditing] = useState<{
    kind: "bid" | "milestone";
    id: string;
    value: string;
    original: string;
    box: { left: number; top: number; width: number; height: number };
  } | null>(null);

  // In-flight bar-end drag: from/to are wrapper-local (CSS px) coords, matching
  // the crosshair/rename overlays' coordinate system so the rubber-band line
  // can be drawn as a plain HTML/SVG overlay element.
  const [drag, setDrag] = useState<{
    bpId: string;
    label: string; // bid-package name, for opening scope on a click-without-drag
    // The pointer that owns this drag. A move/up/cancel from any other pointer
    // is ignored, so a stray/late event can't drive or complete someone else's
    // gesture into an unintended link.
    pointerId: number;
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    // The milestone the drag has snapped to (nearest to the cursor). null until
    // the first move; the release links to exactly this milestone.
    targetMilestoneId: string | null;
  } | null>(null);

  // In-flight milestone drag-to-reschedule. `date` is the day the milestone
  // takes if dropped now: cursor x inverted through the scale, snapped to a
  // whole day, then clamped into [minDate, maxDate] so it can never cross a
  // neighbouring milestone. `atBound` marks a clamped position (the cursor
  // asked for a day the neighbours forbid) — the overlay turns red on it.
  const [msDrag, setMsDrag] = useState<{
    milestoneId: string;
    label: string; // milestone name, for opening scope on a click-without-drag
    pointerId: number;
    x: number; // wrapper-local CSS x for the guide line / date pill
    date: string;
    originalDate: string;
    minDate: string | null;
    maxDate: string | null;
    moved: boolean;
    atBound: boolean;
  } | null>(null);

  // Click-opened actions popover for a milestone diamond: a non-drag way to
  // change its date (validated to the same no-cross window as the drag) plus a
  // shortcut into its scope items. Anchored at the pointer in viewport coords.
  const [scopePopover, setScopePopover] = useState<{
    // Milestone panels carry the date editor; bid panels are scope-only.
    kind: "milestone" | "bid";
    milestoneId: string;
    bidPackageId?: string;
    label: string;
    // Only read when `locked`, where it reports when the milestone happened.
    date: string | null;
    locked: boolean;
    // A stable Dayjs for the picker's value — computed once on open so a
    // re-render (or a refetch that remounts the picker) never hands antd a new
    // object that would resync/reset the open calendar's navigated month.
    dateValue: Dayjs | null;
    min: string | null;
    max: string | null;
    items: string[];
    clientX: number;
    clientY: number;
    // Hover opens the panel unpinned (it auto-closes when the pointer leaves
    // both the diamond and the panel). A click on the diamond or the panel pins
    // it — then only an outside click dismisses it.
    pinned: boolean;
    /** Draft of an in-progress milestone rename (popover name field). */
    nameDraft?: string;
  } | null>(null);

  const addMenuRef = useRef<HTMLDivElement | null>(null);
  const [addMenu, setAddMenu] = useState<{
    placement: AddMilestonePlacement;
    clientX: number;
    clientY: number;
  } | null>(null);

  // Mirrors scopePopover.pinned for handlers that run outside render (the close
  // timer, the hover-open guard) without re-subscribing to state.
  const pinnedRef = useRef(false);
  useEffect(() => {
    pinnedRef.current = Boolean(scopePopover?.pinned);
  }, [scopePopover]);

  // Grace timer: leaving the diamond or the panel schedules a close, and moving
  // onto the other one cancels it — so the pointer can travel the small gap
  // between diamond and panel without it vanishing. A pinned panel never closes
  // this way (only an outside click does).
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPopoverClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const schedulePopoverClose = () => {
    cancelPopoverClose();
    closeTimerRef.current = setTimeout(() => {
      setScopePopover((p) => (p && !p.pinned ? null : p));
    }, 220);
  };
  useEffect(() => () => cancelPopoverClose(), []);

  // The month the calendar is currently showing. Kept in a ref (no re-render)
  // and fed back as defaultPickerValue so the navigated month survives a
  // picker remount instead of snapping back to the value's month.
  const pickerPanelRef = useRef<Dayjs | null>(null);

  // In-progress popover rename: a ref so outside-click close can commit the
  // typed name even if the input unmounts before blur.
  const msRenameDraftRef = useRef<{
    id: string;
    original: string;
    value: string;
  } | null>(null);
  const flushMilestoneRename = useCallback(() => {
    const draft = msRenameDraftRef.current;
    msRenameDraftRef.current = null;
    if (!draft || !onRenameMilestone) return;
    const trimmed = draft.value.trim();
    if (trimmed && trimmed !== draft.original) {
      onRenameMilestone(draft.id, trimmed);
    }
  }, [onRenameMilestone]);

  // Close the actions popover on an outside click — but NOT when the click
  // lands in the date picker's calendar (it portals to <body>, outside the
  // popover), which otherwise dismissed the popover before the pick committed.
  useEffect(() => {
    if (!scopePopover) return undefined;
    const onDocMouseDown = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (scopePopoverRef.current?.contains(target)) return;
      if (target.closest?.(".ant-picker-dropdown")) return;
      // Popconfirm portals to <body>; treating it as outside would dismiss
      // the panel before the user can confirm a milestone delete.
      if (target.closest?.(".ant-popover")) return;
      flushMilestoneRename();
      setScopePopover(null);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [scopePopover, flushMilestoneRename]);

  useEffect(() => {
    if (!addMenu) return undefined;
    const onDocMouseDown = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (addMenuRef.current?.contains(target)) return;
      setAddMenu(null);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [addMenu]);

  const dragActive = Boolean(drag) || Boolean(msDrag);

  // Edge auto-extend: holding the drag inside the last EDGE_ZONE px of the
  // scale keeps advancing the date over TIME (the deeper, the faster), so the
  // first/last milestone can reach dates far beyond the drawn domain without
  // running out of pixels — the scale refits around the new date on drop.
  // Interval-driven; all mutable bookkeeping lives in a ref so pointermove can
  // update it without re-subscribing.
  const edgeExtendRef = useRef<{
    timer: number | null;
    dir: -1 | 1;
    depth: number; // 0..1, how deep into the edge zone the pointer sits
    base: string; // the pointer-derived day the extension counts from
    extra: number; // accumulated extra days
  }>({ timer: null, dir: 1, depth: 0, base: "", extra: 0 });

  const stopEdgeExtend = () => {
    const er = edgeExtendRef.current;
    if (er.timer != null) {
      window.clearInterval(er.timer);
      er.timer = null;
    }
    er.extra = 0;
    er.depth = 0;
  };

  // Clear a live interval if the component unmounts mid-drag.
  useEffect(
    () => () => {
      const er = edgeExtendRef.current;
      if (er.timer != null) window.clearInterval(er.timer);
    },
    []
  );

  if (plannerLayout) {
    return (
      <PlannerLaneBody
        geometry={geometry}
        config={config}
        onAddMilestone={onAddMilestone}
        onMilestoneReschedule={onMilestoneReschedule}
        onMilestoneActualDate={onMilestoneActualDate}
        onRenameMilestone={onRenameMilestone}
        onDeleteMilestone={onDeleteMilestone}
        milestoneScopeItems={milestoneScopeItems}
        onMilestoneScopeOpen={onMilestoneScopeOpen}
        bidScopeItems={bidScopeItems}
        onBidScopeOpen={onBidScopeOpen}
        onLinkChange={onLinkChange}
        footerSlot={footerSlot}
        crosshair={crosshair}
        projectId={projectId}
        designPackageName={designPackageName}
        onSetRequiredOnSite={onSetRequiredOnSite}
        onManageBidPackages={onManageBidPackages}
        onCreatePlan={onCreatePlan}
        printMode={printMode}
        fullTimeline={fullTimeline}
      />
    );
  }

  const beginRename = (
    kind: "bid" | "milestone",
    id: string,
    currentName: string,
    rect: DOMRect
  ) => {
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;
    const wrapperRect = wrapperEl.getBoundingClientRect();
    setScopePopover(null);
    setEditing({
      kind,
      id,
      value: currentName,
      original: currentName,
      box: {
        left: rect.left - wrapperRect.left,
        top: rect.top - wrapperRect.top,
        width: rect.width,
        height: rect.height
      }
    });
  };

  const handleNameClick = onRenameBidPackage
    ? (bpId: string, currentTitle: string, rect: DOMRect) =>
        beginRename("bid", bpId, currentTitle, rect)
    : undefined;

  const handleMilestoneNameClick = onRenameMilestone
    ? (milestoneId: string, currentName: string, rect: DOMRect) =>
        beginRename("milestone", milestoneId, currentName, rect)
    : undefined;

  // Enter and blur both commit; guarding on the current value (via functional
  // update) means a stray blur after Enter/Escape already cleared it is a
  // no-op instead of a second, stale save.
  const commitRename = () => {
    setEditing((current) => {
      if (!current) return current;
      const trimmed = current.value.trim();
      if (trimmed && trimmed !== current.original) {
        if (current.kind === "milestone") {
          onRenameMilestone?.(current.id, trimmed);
        } else {
          onRenameBidPackage?.(current.id, trimmed);
        }
      }
      return null;
    });
  };

  // Cursor → date: undo the CSS scaling the SVG's width:100% + viewBox
  // introduces (css x → geometry x), then undo the labelColumn offset the
  // renderer adds on top of the scale's own coordinate space (geometry x →
  // dateScale x) before asking the scale to invert it back to a date.
  const handleCrosshairMove = (e: MouseEvent<HTMLDivElement>) => {
    // While a drag is in flight its own guide/pill owns the overlay — a second
    // crosshair line and date pill underneath would just contradict it.
    if (dragActive) {
      setCross(null);
      return;
    }
    const mapped = pointerToGeometry(e.clientX);
    if (!mapped) return;
    const { cssX, geomX } = mapped;
    const dsX = geomX - config.labelColumnWidth;
    const scaleEnd =
      geometry.width - config.labelColumnWidth - config.clusterWidth;
    if (dsX < 0 || dsX > scaleEnd) {
      setCross(null);
      return; // over the label or cluster columns — no date under the cursor
    }
    const date = geometry.xToDate(dsX);
    setCross(date ? { x: cssX, date } : null);
  };

  const handleTimelineClick = onAddMilestone
    ? (e: MouseEvent<HTMLDivElement>) => {
        if (dragActive || editing) return;
        const target = e.target as Element | null;
        if (
          target?.closest?.(
            '[data-testid^="milestone-"], [data-testid^="ms-name"], [data-testid^="bp-"], [data-testid="ms-date-popover"], [data-testid="ms-add-menu"]'
          )
        ) {
          return;
        }
        const mapped = pointerToGeometry(e.clientX);
        if (!mapped) return;
        const dsX = mapped.geomX - config.labelColumnWidth;
        const scaleEnd =
          geometry.width - config.labelColumnWidth - config.clusterWidth;
        if (dsX < 0 || dsX > scaleEnd) return;
        const raw = geometry.xToDate(dsX);
        if (!raw) return;
        const placement = placementForDate(
          geometry.axis.ticks.map((t) => t.milestone),
          dayOf(raw)
        );
        if (!placement) return;
        setScopePopover(null);
        setAddMenu({
          placement,
          clientX: e.clientX,
          clientY: e.clientY
        });
      }
    : undefined;

  // Pointer-down on a bar-end handle starts the drag. The handle's own
  // getBoundingClientRect (not the geometry math) anchors the rubber-band
  // line's start point — the same trick the rename input uses to place itself
  // without reimplementing the SVG's viewBox/scroll math.
  const handleLinkHandleDown = onLinkChange
    ? (bpId: string, e: PointerEvent<SVGPathElement>) => {
        e.preventDefault(); // don't start a text selection while dragging
        const handle = e.currentTarget;
        handle.setPointerCapture(e.pointerId);
        const wrapperEl = wrapperRef.current;
        const wrapperRect = wrapperEl?.getBoundingClientRect();
        // Anchor the rubber-band to the pointer's own down-position, not the
        // grabbed element's bbox centre: the connector grab target is an L-shaped
        // path whose bbox centre floats in the empty elbow, off the visible line.
        const fromX = wrapperRect ? e.clientX - wrapperRect.left : 0;
        const fromY = wrapperRect ? e.clientY - wrapperRect.top : 0;
        const label =
          geometry.rows.find((row) => row.row.id === bpId)?.row.name ?? "";
        setDrag({
          bpId,
          label,
          pointerId: e.pointerId,
          fromX,
          fromY,
          toX: fromX,
          toY: fromY,
          targetMilestoneId: null
        });
      }
    : undefined;

  // The design milestone whose diamond is nearest a given geometry-x (screen x
  // of a tick = labelColumnWidth + tick.x). The drag snaps its target to this.
  const nearestMilestone = (geomX: number): AxisTick | null => {
    let nearest: AxisTick | null = null;
    let nearestDist = Infinity;
    for (const tick of geometry.axis.ticks) {
      const dist = Math.abs(config.labelColumnWidth + tick.x - geomX);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = tick;
      }
    }
    return nearest;
  };

  // While dragging, snap the line's end to the NEAREST milestone (not the raw
  // cursor) and re-render only when that nearest milestone changes. Snapping
  // gives the "jump to the nearest milestone" feel; the change-guard keeps the
  // heavy SVG from re-rendering on every pixel, so the drag stays smooth.
  const handleLinkPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const mapped = pointerToGeometry(e.clientX);
    if (!mapped) return;
    const { geomX, scale } = mapped;
    const nearest = nearestMilestone(geomX);
    if (!nearest) return;
    setDrag((current) =>
      !current || current.targetMilestoneId === nearest.milestone.id
        ? current
        : {
            ...current,
            toX: (config.labelColumnWidth + nearest.x) * scale,
            toY: DIAMOND_CY * scale,
            targetMilestoneId: nearest.milestone.id
          }
    );
  };

  // Drop: link to the milestone the drag snapped to (the highlighted one under
  // the line's end). A press with no drag never sets a target, so it links
  // nothing — a plain click can't accidentally re-wire a bid package.
  const handleLinkPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const { bpId, label, targetMilestoneId } = drag;
    setDrag(null);
    if (targetMilestoneId) {
      onLinkChange?.(bpId, targetMilestoneId);
    } else {
      // A press that never snapped to a milestone is a click, not a link:
      // pin open the bid package's scope panel.
      openBidPanel(
        { bidPackageId: bpId, label, clientX: e.clientX, clientY: e.clientY },
        true
      );
    }
  };

  // A canceled pointer (touch/trackpad pan taken over for scrolling, a
  // context menu, alt-tab) fires pointercancel instead of pointerup and
  // implicitly releases capture. Abort the drag WITHOUT linking — otherwise the
  // stale drag state would let a later, unrelated click complete into a link
  // the user never intended.
  const handleLinkPointerCancel = (e: PointerEvent<HTMLDivElement>) => {
    setDrag((current) =>
      current && e.pointerId === current.pointerId ? null : current
    );
  };

  // ---- design-milestone drag-to-reschedule ----------------------------------

  // No-cross bounds: the dragged milestone must stay strictly between its
  // NEAREST dated workflow neighbours (by sequence_no) — one day after the
  // closest earlier one, one day before the closest later one. Locked
  // (actual-dated) neighbours count too: you can't drag past a milestone that
  // already happened. Deliberately nearest-only, not min/max over ALL
  // milestones: real data carries out-of-order dates (e.g. a sequence-1
  // milestone whose ACTUAL landed after everything), and letting a far
  // milestone dominate the bound would freeze the whole package — with
  // nearest-only, just the milestones directly contradicted by their own
  // neighbours are stuck.
  const milestoneBounds = (target: MilestoneNode) => {
    let prev: MilestoneNode | null = null;
    let next: MilestoneNode | null = null;
    for (const tick of geometry.axis.ticks) {
      const m = tick.milestone;
      if (m.id === target.id || !m.effectiveDate) continue;
      if (m.sequenceNo < target.sequenceNo) {
        if (!prev || m.sequenceNo > prev.sequenceNo) prev = m;
      } else if (m.sequenceNo > target.sequenceNo) {
        if (!next || m.sequenceNo < next.sequenceNo) next = m;
      }
    }
    return {
      min: prev?.effectiveDate
        ? addDaysIso(dayOf(prev.effectiveDate), 1)
        : null,
      max: next?.effectiveDate
        ? addDaysIso(dayOf(next.effectiveDate), -1)
        : null
    };
  };

  // Open the milestone actions panel: look up the milestone, compute its
  // no-cross date window (same as the drag), and anchor at the pointer. `pinned`
  // distinguishes a hover-open (auto-closes) from a click-open (sticks until an
  // outside click). A hover never replaces an already-pinned panel.
  const openMilestonePanel = (
    info: {
      milestoneId: string;
      label: string;
      clientX: number;
      clientY: number;
    },
    pinned: boolean
  ) => {
    if (!pinned && pinnedRef.current) return;
    // Undated milestones have no tick, so they are only reachable through the
    // tray below the timeline — look there too.
    const ms =
      geometry.axis.ticks.find((t) => t.milestone.id === info.milestoneId)
        ?.milestone ??
      geometry.undatedMilestones.find((m) => m.id === info.milestoneId);
    if (!ms) return;
    const { min, max } = milestoneBounds(ms);
    cancelPopoverClose();
    pickerPanelRef.current = null; // fresh calendar view for this milestone
    if (msRenameDraftRef.current && msRenameDraftRef.current.id !== ms.id) {
      flushMilestoneRename();
    }
    msRenameDraftRef.current = onRenameMilestone
      ? { id: ms.id, original: info.label, value: info.label }
      : null;
    setScopePopover({
      kind: "milestone",
      milestoneId: ms.id,
      label: info.label,
      nameDraft: info.label,
      // "Completed {date}" reports the day it happened, not the commitment it
      // was made against — those differ whenever a milestone landed off plan.
      date: ms.actualDate ?? ms.effectiveDate,
      dateValue: ms.effectiveDate ? dayjs(dayOf(ms.effectiveDate)) : null,
      // An actual date means the milestone has happened, so the date is frozen.
      // A milestone with no committed date is editable — the picker is how it
      // gets its first one (it opens empty, bounded by its dated neighbours).
      locked: Boolean(ms.actualDate),
      min,
      max,
      items: milestoneScopeItems?.[ms.id] ?? [],
      clientX: info.clientX,
      clientY: info.clientY,
      pinned
    });
  };

  // A click on the diamond pins the panel open.
  const handleMilestoneOpen = (info: {
    milestoneId: string;
    label: string;
    clientX: number;
    clientY: number;
  }) => openMilestonePanel(info, true);

  // Hovering a diamond opens the panel unpinned; leaving schedules its close.
  const handleMilestoneHover = (info: ScopeHoverInfo | null) => {
    if (!info?.milestoneId) {
      schedulePopoverClose();
      return;
    }
    openMilestonePanel(
      {
        milestoneId: info.milestoneId,
        label: info.label,
        clientX: info.clientX,
        clientY: info.clientY
      },
      false
    );
  };

  // Bid-package panel: the same hover→pin surface, minus the date editor (a bid
  // package has no reschedulable date here — only its scope items).
  const openBidPanel = (
    info: {
      bidPackageId: string;
      label: string;
      clientX: number;
      clientY: number;
    },
    pinned: boolean
  ) => {
    if (!pinned && pinnedRef.current) return;
    cancelPopoverClose();
    setScopePopover({
      kind: "bid",
      milestoneId: "",
      bidPackageId: info.bidPackageId,
      label: info.label,
      date: null,
      dateValue: null,
      locked: true,
      min: null,
      max: null,
      items: bidScopeItems?.[info.bidPackageId] ?? [],
      clientX: info.clientX,
      clientY: info.clientY,
      pinned
    });
  };

  const handleBidHover = (info: ScopeHoverInfo | null) => {
    if (!info?.bidPackageId) {
      schedulePopoverClose();
      return;
    }
    openBidPanel(
      {
        bidPackageId: info.bidPackageId,
        label: info.label,
        clientX: info.clientX,
        clientY: info.clientY
      },
      false
    );
  };

  const handleMilestoneDown =
    milestoneDragEnabled && onMilestoneReschedule
      ? (milestone: MilestoneNode, e: PointerEvent<SVGCircleElement>) => {
          if (!milestone.effectiveDate) return;
          e.preventDefault(); // no text selection / native drag while dragging
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            // Capture is an enhancement (keeps events flowing off-wrapper);
            // synthetic/automation pointers can't be captured and throw — the
            // wrapper-level move/up listeners keep the drag working without it.
          }
          const wrapperRect = wrapperRef.current?.getBoundingClientRect();
          const { min, max } = milestoneBounds(milestone);
          const day = dayOf(milestone.effectiveDate);
          setMsDrag({
            milestoneId: milestone.id,
            label: milestone.name,
            pointerId: e.pointerId,
            x: wrapperRect ? e.clientX - wrapperRect.left : 0,
            date: day,
            originalDate: day,
            minDate: min,
            maxDate: max,
            moved: false,
            atBound: false
          });
        }
      : undefined;

  // One edge-extend step: bump the accumulated days (faster the deeper the
  // pointer sits in the zone) and re-clamp. Bookkeeping lives in the ref and
  // state is read functionally, so the interval never sees a stale closure.
  const edgeTick = () => {
    const er = edgeExtendRef.current;
    er.extra += Math.max(1, Math.round(er.depth * 3));
    setMsDrag((current) => {
      if (!current) return current;
      const { date, atBound } = clampToBounds(
        addDaysIso(er.base, er.dir * er.extra),
        current.minDate,
        current.maxDate,
        current.originalDate
      );
      if (date === current.date && atBound === current.atBound) return current;
      return { ...current, date, atBound, moved: true };
    });
  };

  const handleMsPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!msDrag || e.pointerId !== msDrag.pointerId) return;
    const mapped = pointerToGeometry(e.clientX);
    if (!mapped) return;
    const { cssX, geomX } = mapped;
    // Same css → geometry → dateScale transform as the crosshair, but clamped
    // into the scale region so dragging over the label/cluster columns keeps
    // resolving to the nearest scale end instead of dropping the drag.
    const scaleEnd =
      geometry.width - config.labelColumnWidth - config.clusterWidth;
    const dsX = Math.min(
      Math.max(geomX - config.labelColumnWidth, 0),
      scaleEnd
    );
    const raw = geometry.xToDate(dsX);
    if (!raw) return;

    // Edge zones: while the pointer holds inside either end of the scale, an
    // interval keeps extending the date beyond what the pixels can express.
    // Leaving the zone stops the interval and drops the accumulated extension,
    // so the date snaps back to tracking the pointer.
    const er = edgeExtendRef.current;
    const rightDepth =
      dsX > scaleEnd - EDGE_ZONE
        ? (dsX - (scaleEnd - EDGE_ZONE)) / EDGE_ZONE
        : 0;
    const leftDepth = dsX < EDGE_ZONE ? (EDGE_ZONE - dsX) / EDGE_ZONE : 0;
    if (rightDepth > 0 || leftDepth > 0) {
      er.dir = rightDepth > 0 ? 1 : -1;
      er.depth = Math.min(Math.max(rightDepth, leftDepth), 1);
      er.base = raw;
      if (er.timer == null) {
        er.timer = window.setInterval(edgeTick, EDGE_TICK_MS);
      }
    } else {
      stopEdgeExtend();
    }

    const candidate = er.extra ? addDaysIso(raw, er.dir * er.extra) : raw;
    const { date, atBound } = clampToBounds(
      candidate,
      msDrag.minDate,
      msDrag.maxDate,
      msDrag.originalDate
    );
    setMsDrag((current) =>
      current ? { ...current, x: cssX, date, atBound, moved: true } : current
    );
  };

  // Drop: persist only a real move to a different day. A plain click (no move)
  // or a drop back on the original day writes nothing.
  const handleMsPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!msDrag || e.pointerId !== msDrag.pointerId) return;
    stopEdgeExtend();
    const { milestoneId, label, date, originalDate, moved } = msDrag;
    setMsDrag(null);
    if (moved && date !== originalDate) {
      onMilestoneReschedule?.(milestoneId, date);
    } else if (!moved) {
      // A press that never moved is a click: open the milestone actions popover.
      handleMilestoneOpen({
        milestoneId,
        label,
        clientX: e.clientX,
        clientY: e.clientY
      });
    }
  };

  const handleMsPointerCancel = (e: PointerEvent<HTMLDivElement>) => {
    setMsDrag((current) => {
      if (current && e.pointerId === current.pointerId) {
        stopEdgeExtend();
        return null;
      }
      return current;
    });
  };

  // Both drag interactions share the wrapper's pointer listeners; each handler
  // no-ops unless its own drag (matched by pointerId) is in flight.
  const dragEnabled = Boolean(onLinkChange) || Boolean(onMilestoneReschedule);
  const handleAnyPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    handleLinkPointerMove(e);
    handleMsPointerMove(e);
  };
  const handleAnyPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    handleLinkPointerUp(e);
    handleMsPointerUp(e);
  };
  const handleAnyPointerCancel = (e: PointerEvent<HTMLDivElement>) => {
    handleLinkPointerCancel(e);
    handleMsPointerCancel(e);
  };

  const arrowMarker = (
    <defs>
      <marker
        id="dep-arrow"
        markerWidth={6}
        markerHeight={6}
        refX={5}
        refY={3}
        orient="auto"
      >
        <path d="M0 0L6 3L0 6Z" fill={palette.arrow} />
      </marker>
    </defs>
  );

  // Design package milestones pin to the top, the month/time ruler pins to the
  // bottom, and only the bid-package rows scroll between them. position:sticky
  // keeps both pinned vertically while moving horizontally with the content, so
  // they stay x-aligned with the rows. The legend is an always-visible footer.
  const opensMilestonePanel =
    Boolean(onMilestoneScopeOpen) ||
    Boolean(onMilestoneReschedule) ||
    Boolean(onDeleteMilestone) ||
    Boolean(onRenameMilestone);

  const headerSvg = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${geometry.width} ${geometry.axisHeight}`}
      preserveAspectRatio="xMinYMin meet"
      style={{
        display: "block",
        width: "100%",
        height: "auto",
        background: palette.surface
      }}
    >
      {/* surface + muted panel both fill the band from y=0 (top padding is
          baked into the content positions, no translate needed) */}
      <rect
        x={0}
        y={0}
        width="100%"
        height={geometry.axisHeight}
        fill={palette.surface}
      />
      <rect
        x={0}
        y={0}
        width={config.labelColumnWidth}
        height={geometry.axisHeight}
        fill={palette.panelBg}
      />
      {/* timeline-row background first (behind), then the tall design
          track overlaps it, then the scale + labels go on top */}
      <TimelineBand geometry={geometry} config={config} />
      <HeaderBand
        geometry={geometry}
        scaleX={scaleX}
        config={config}
        onMilestoneDown={handleMilestoneDown}
        milestoneScopeItems={milestoneScopeItems}
        onMilestoneActivate={
          opensMilestonePanel ? handleMilestoneOpen : undefined
        }
        onMilestoneScopeHover={
          opensMilestonePanel ? handleMilestoneHover : undefined
        }
        onMilestoneNameClick={handleMilestoneNameClick}
      />
      <TimelineScale geometry={geometry} scaleX={scaleX} config={config} />
      {/* divider separating the design band from the scrolling rows */}
      <line
        x1={0}
        y1={geometry.axisHeight - 0.5}
        x2="100%"
        y2={geometry.axisHeight - 0.5}
        stroke={palette.divider}
        strokeWidth={1}
      />
    </svg>
  );

  const scrollBlock = (
    <div
      ref={scrollRef}
      className="ciq-dep-scroll"
      data-testid="dep-scroll"
      style={{
        maxHeight: "60vh",
        overflowX: "hidden", // the SVG scales to fit — no horizontal scroll
        overflowY: "auto",
        position: "relative"
      }}
    >
      {/* sticky top: design package band (milestones + bar) */}
      <div
        className="ciq-dep-sticky"
        style={{ position: "sticky", top: 0, zIndex: 3 }}
      >
        {headerSvg}
      </div>

      {/* bid-package rows — scales by the same factor as the header band, so
          both stay x-aligned while fitting the container width */}
      {/* scales by the same viewBox intrinsic width as the header so both bands
          stay x-aligned while filling the card body width */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${geometry.width} ${bodyHeight}`}
        preserveAspectRatio="xMinYMin meet"
        style={{
          display: "block",
          width: "100%",
          height: "auto"
        }}
      >
        {arrowMarker}
        <rect
          x={0}
          y={0}
          width={config.labelColumnWidth}
          height={bodyHeight}
          fill={palette.panelBg}
        />
        <Gridlines
          gridlines={geometry.gridlines}
          scaleX={scaleX}
          height={bodyHeight}
          config={config}
        />
        {/* the cluster-column separator continues through the rows so the
            boundary of the drag surface reads as one line top to bottom.
            Interactive consumers only — the details page renders unchanged. */}
        {onMilestoneReschedule && (
          <line
            x1={geometry.width - config.clusterWidth}
            y1={0}
            x2={geometry.width - config.clusterWidth}
            y2={bodyHeight}
            stroke={palette.divider}
            strokeWidth={1.5}
          />
        )}
        {/* rows are positioned in full-geometry coords; shift up so the body
            starts at the first row (just under the sticky design band). */}
        <g transform={`translate(0, ${-geometry.axisHeight})`}>
          {geometry.rows.map((g) => (
            <BidPackageDepLayer
              key={`dep-${g.row.id}`}
              g={g}
              scaleX={scaleX}
              totalWidth={geometry.width}
              breaks={breakXs}
              config={config}
              onLinkHandleDown={handleLinkHandleDown}
            />
          ))}
          {geometry.rows.map((g) => (
            <BidPackageContent
              key={`row-${g.row.id}`}
              g={g}
              scaleX={scaleX}
              totalWidth={geometry.width}
              config={config}
              onNameClick={handleNameClick}
              onLinkHandleDown={handleLinkHandleDown}
              scopeItems={bidScopeItems?.[g.row.id]}
              onScopeHover={onBidScopeOpen ? handleBidHover : undefined}
            />
          ))}
        </g>
      </svg>
    </div>
  );

  // None of the opt-in consumers need the wrapper, so it only mounts when one
  // of them is actually turned on — the details-page consumer (no prop set)
  // stays wrapper-free, matching its render before any of these features existed.
  const needsWrapper =
    crosshair ||
    Boolean(onRenameBidPackage) ||
    Boolean(onRenameMilestone) ||
    Boolean(onDeleteMilestone) ||
    Boolean(onAddMilestone) ||
    Boolean(onLinkChange) ||
    Boolean(onMilestoneReschedule) ||
    Boolean(onMilestoneScopeOpen) ||
    Boolean(onBidScopeOpen);

  // Uncommitted milestones count too — they are named milestones of this
  // package, just without a date, so the "needs at least two milestones" delete
  // guard must see them (the same set Lane's own guard counts).
  const namedMilestoneCount =
    geometry.axis.ticks.filter((t) => Boolean(t.milestone.name)).length +
    geometry.undatedMilestones.length;

  // The timeline plots committed dates, so a milestone without one has no x and
  // is listed here instead of vanishing from the package. A chip opens the same
  // actions panel a diamond does, where the date picker commits its first date.
  // Read-only when the consumer wires no reschedule callback (the details page,
  // and print).
  const undatedTray = geometry.undatedMilestones.length > 0 && (
    <div
      data-testid="undated-milestones"
      className="flex flex-wrap items-center gap-2 px-3 py-2"
      style={{
        borderTop: `1px solid ${palette.divider}`,
        fontFamily: config.fonts.family,
        fontSize: config.fonts.body,
        breakInside: "avoid"
      }}
    >
      <span style={{ color: palette.textMuted, fontWeight: 600 }}>
        No committed date ({geometry.undatedMilestones.length})
      </span>
      {geometry.undatedMilestones.map((m) =>
        onMilestoneReschedule ? (
          <button
            key={m.id}
            type="button"
            data-testid={`undated-ms-${m.id}`}
            title={`${m.name} — click to set a committed date`}
            className="cursor-pointer rounded-full bg-transparent px-2 py-0.5"
            style={{
              border: `1px dashed ${palette.designPackage}`,
              color: palette.designPackage,
              fontSize: config.fonts.body
            }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              openMilestonePanel(
                {
                  milestoneId: m.id,
                  label: m.name,
                  clientX: rect.left + rect.width / 2,
                  clientY: rect.bottom
                },
                true
              );
            }}
          >
            {m.name}
          </button>
        ) : (
          <span
            key={m.id}
            data-testid={`undated-ms-${m.id}`}
            className="rounded-full px-2 py-0.5"
            style={{
              border: `1px dashed ${palette.divider}`,
              color: palette.textMuted
            }}
          >
            {m.name}
          </span>
        )
      )}
    </div>
  );
  const linkedBidCountForOpenMs = scopePopover
    ? geometry.rows.filter(
        (r) => r.linkBadge?.milestoneId === scopePopover.milestoneId
      ).length
    : 0;

  function dependencyTimelineBody() {
    return (
      <div>
        {needsWrapper ? (
          // The date scale is a canvas-like surface; click opens the add-milestone
          // menu at the pointer.
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <div
            ref={wrapperRef}
            data-testid="dep-crosshair-wrapper"
            style={{ position: "relative" }}
            onMouseMove={crosshair ? handleCrosshairMove : undefined}
            onMouseLeave={crosshair ? () => setCross(null) : undefined}
            onClick={handleTimelineClick}
            onPointerMove={dragEnabled ? handleAnyPointerMove : undefined}
            onPointerUp={dragEnabled ? handleAnyPointerUp : undefined}
            onPointerCancel={dragEnabled ? handleAnyPointerCancel : undefined}
          >
            {scrollBlock}
            {/* overlay stays pointer-events-none so the SVG's native <title>
              tooltips underneath remain hoverable; the drag handle itself
              captures its own pointer events (see onLinkHandleDown) so it isn't
              affected by that. */}
            {(cross || drag || msDrag) && (
              <div className="pointer-events-none absolute inset-0 z-10">
                {/* guide + date pill for the in-flight milestone drag; the pill
                  shows the CLAMPED day, so hitting a neighbour's bound reads as
                  the date freezing while the cursor keeps going */}
                {msDrag && (
                  <>
                    {/* red = the drag is clamped at a neighbour's bound (the
                      cursor asked for a day the no-cross rule refuses) */}
                    <div
                      data-testid="milestone-drag-line"
                      className="absolute top-0 bottom-0"
                      style={{
                        left: msDrag.x,
                        width: msDrag.atBound ? 2 : 1,
                        background: msDrag.atBound
                          ? palette.danger
                          : palette.designPackage
                      }}
                    />
                    <div
                      data-testid="milestone-drag-date"
                      data-bound={msDrag.atBound || undefined}
                      className="absolute whitespace-nowrap rounded px-1.5 py-0.5"
                      style={{
                        left: msDrag.x,
                        top: 4,
                        transform: "translateX(-50%)",
                        background: palette.surface,
                        border: `1.5px solid ${
                          msDrag.atBound
                            ? palette.danger
                            : palette.designPackage
                        }`,
                        color: msDrag.atBound ? palette.danger : palette.text,
                        fontFamily: config.fonts.family,
                        fontSize: 11,
                        fontWeight: 600
                      }}
                    >
                      {fmtDate(msDrag.date)}
                      {msDrag.atBound ? " — can't cross a milestone" : ""}
                    </div>
                  </>
                )}
                {cross && (
                  <>
                    <div
                      data-testid="dep-crosshair"
                      className="absolute top-0 bottom-0"
                      style={{
                        left: cross.x,
                        width: 1,
                        background: palette.dependency
                      }}
                    />
                    <div
                      data-testid="dep-crosshair-date"
                      className="absolute whitespace-nowrap rounded px-1.5 py-0.5"
                      style={{
                        left: cross.x,
                        top: 4,
                        transform: "translateX(-50%)",
                        background: palette.surface,
                        border: `1px solid ${palette.divider}`,
                        color: palette.text,
                        fontFamily: config.fonts.family,
                        fontSize: 11
                      }}
                    >
                      {fmtDate(cross.date)}
                    </div>
                  </>
                )}
                {/* rubber-band line for the in-flight bar-end → milestone drag */}
                {drag && (
                  <svg className="absolute inset-0 h-full w-full">
                    <line
                      data-testid="bp-link-drag-line"
                      x1={drag.fromX}
                      y1={drag.fromY}
                      x2={drag.toX}
                      y2={drag.toY}
                      stroke={palette.dependency}
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                    />
                    {/* ring on the milestone the line has snapped to, so the drop
                      target is unmistakable before release */}
                    {drag.targetMilestoneId != null && (
                      <circle
                        data-testid="bp-link-drop-target"
                        cx={drag.toX}
                        cy={drag.toY}
                        r={10}
                        fill="none"
                        stroke={palette.designPackage}
                        strokeWidth={2}
                      />
                    )}
                  </svg>
                )}
              </div>
            )}
            {/* Shared actions panel for a milestone or bid-package diamond: opens
              on hover, pins on click, dismisses on an outside click. Milestone
              panels add a date editor (validated to the no-cross window); bid
              panels are scope-only. Sits outside the pointer-events-none overlay
              so its controls work. */}
            {scopePopover && (
              // The panel's actual controls (close, date picker, scope button) are
              // native, keyboard-reachable elements; these div-level handlers are
              // mouse-only hover/pin niceties, not the sole path to any action.
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <div
                ref={scopePopoverRef}
                data-testid="ms-date-popover"
                className="fixed z-50 overflow-hidden rounded-lg"
                // Moving onto the panel keeps it open; leaving it (unpinned)
                // schedules a close. Any press inside pins it open.
                onMouseEnter={cancelPopoverClose}
                onMouseLeave={schedulePopoverClose}
                onMouseDown={() => {
                  cancelPopoverClose();
                  setScopePopover((p) =>
                    p && !p.pinned ? { ...p, pinned: true } : p
                  );
                }}
                style={{
                  left: scopePopover.clientX,
                  // Flip above the diamond when there isn't room below, so a
                  // milestone near the viewport bottom isn't clipped.
                  ...(scopePopover.clientY < window.innerHeight - 300
                    ? {
                        top: scopePopover.clientY + 14,
                        transform: "translateX(-50%)"
                      }
                    : {
                        top: scopePopover.clientY - 14,
                        transform: "translate(-50%, -100%)"
                      }),
                  minWidth: 264,
                  maxWidth: 300,
                  background: palette.surface,
                  border: `1px solid ${palette.divider}`,
                  boxShadow: "0 8px 28px var(--ciq-black-a16)",
                  fontFamily: config.fonts.family
                }}
              >
                <div
                  className="flex items-center gap-2 px-3 py-2"
                  style={{
                    // Colour the header by kind to match each entity's mark: the
                    // design milestone's purple, or the bid package's teal bar.
                    background:
                      scopePopover.kind === "bid"
                        ? palette.bidPackage
                        : palette.designPackage,
                    color: palette.surface
                  }}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                    {scopePopover.label}
                  </span>
                  <button
                    type="button"
                    aria-label="Close"
                    className="ml-auto cursor-pointer border-0 bg-transparent p-0"
                    style={{ color: palette.surface, lineHeight: 1 }}
                    onClick={() => {
                      flushMilestoneRename();
                      setScopePopover(null);
                    }}
                  >
                    ×
                  </button>
                </div>
                {scopePopover.kind === "milestone" && onRenameMilestone && (
                  <div className="px-3 py-2.5">
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        color: palette.textMuted,
                        marginBottom: 6
                      }}
                    >
                      MILESTONE NAME
                    </div>
                    <Input
                      size="small"
                      data-testid="ms-popover-name"
                      value={scopePopover.nameDraft ?? scopePopover.label}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (msRenameDraftRef.current) {
                          msRenameDraftRef.current.value = value;
                        }
                        setScopePopover((p) =>
                          p ? { ...p, nameDraft: value } : p
                        );
                      }}
                      onPressEnter={() => {
                        flushMilestoneRename();
                        setScopePopover(null);
                      }}
                    />
                  </div>
                )}
                {scopePopover.kind === "milestone" && onMilestoneReschedule && (
                  <div
                    className="px-3 py-2.5"
                    style={{
                      borderTop: onRenameMilestone
                        ? `1px solid ${palette.divider}`
                        : undefined
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        color: palette.textMuted,
                        marginBottom: 6
                      }}
                    >
                      MILESTONE DATE
                    </div>
                    {scopePopover.locked ? (
                      <div style={{ fontSize: 12.5, color: palette.text }}>
                        Completed {fmtDate(scopePopover.date)} — date locked
                      </div>
                    ) : (
                      <>
                        <DatePicker
                          value={scopePopover.dateValue}
                          format={DATE_FORMAT_MMDDYYYY}
                          allowClear={false}
                          style={{ width: "100%" }}
                          // Portal the calendar to <body> so the popover's
                          // overflow-hidden can't clip it.
                          getPopupContainer={() => document.body}
                          // Keep the navigated month across any picker remount.
                          defaultPickerValue={
                            pickerPanelRef.current ??
                            scopePopover.dateValue ??
                            undefined
                          }
                          onPanelChange={(v) => {
                            pickerPanelRef.current = v;
                          }}
                          disabledDate={(d) => {
                            const iso = d.format("YYYY-MM-DD");
                            if (scopePopover.min && iso < scopePopover.min)
                              return true;
                            if (scopePopover.max && iso > scopePopover.max)
                              return true;
                            return false;
                          }}
                          onChange={(d) => {
                            if (d) {
                              flushMilestoneRename();
                              onMilestoneReschedule(
                                scopePopover.milestoneId,
                                d.format("YYYY-MM-DD")
                              );
                              setScopePopover(null);
                            }
                          }}
                        />
                        <div
                          style={{
                            fontSize: 11,
                            color: palette.textMuted,
                            marginTop: 5
                          }}
                        >
                          {scopePopover.min && scopePopover.max
                            ? `Allowed ${fmtDate(scopePopover.min)} → ${fmtDate(scopePopover.max)}`
                            : scopePopover.min
                              ? `On or after ${fmtDate(scopePopover.min)}`
                              : scopePopover.max
                                ? `On or before ${fmtDate(scopePopover.max)}`
                                : "Any date"}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {((scopePopover.kind === "milestone" && onMilestoneScopeOpen) ||
                  (scopePopover.kind === "bid" && onBidScopeOpen)) && (
                  <div
                    className="px-3 py-2.5"
                    style={{
                      borderTop:
                        scopePopover.kind === "milestone" &&
                        (onMilestoneReschedule || onRenameMilestone)
                          ? `1px solid ${palette.divider}`
                          : undefined
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: palette.textMuted,
                        marginBottom: 6
                      }}
                    >
                      {scopePopover.items.length} scope item
                      {scopePopover.items.length === 1 ? "" : "s"}
                    </div>
                    <Button
                      block
                      size="small"
                      data-testid="ms-popover-scope"
                      onClick={() => {
                        if (
                          scopePopover.kind === "bid" &&
                          scopePopover.bidPackageId
                        ) {
                          const bpRow = geometry.rows.find(
                            (r) => r.row.id === scopePopover.bidPackageId
                          );
                          onBidScopeOpen?.({
                            bidPackageId: scopePopover.bidPackageId,
                            label: scopePopover.label,
                            milestoneName:
                              bpRow?.linkBadge?.milestoneName ??
                              bpRow?.row.linkedDpMilestone?.name ??
                              undefined
                          });
                        } else {
                          onMilestoneScopeOpen?.({
                            milestoneId: scopePopover.milestoneId,
                            label: scopePopover.label
                          });
                        }
                        setScopePopover(null);
                      }}
                    >
                      View / add scope items
                    </Button>
                  </div>
                )}
                {scopePopover.kind === "milestone" && onDeleteMilestone && (
                  <div
                    className="px-3 py-2.5"
                    style={{
                      borderTop:
                        onRenameMilestone ||
                        onMilestoneReschedule ||
                        onMilestoneScopeOpen
                          ? `1px solid ${palette.divider}`
                          : undefined
                    }}
                  >
                    <Popconfirm
                      title={`Delete "${scopePopover.label}"?`}
                      description={
                        namedMilestoneCount <= 2
                          ? "A design package needs at least two milestones."
                          : linkedBidCountForOpenMs
                            ? `${linkedBidCountForOpenMs} Bid Package${
                                linkedBidCountForOpenMs === 1 ? "" : "s"
                              } currently link to this milestone.`
                            : "This cannot be undone."
                      }
                      okText="Delete"
                      okButtonProps={{
                        danger: true,
                        className: "ciq-delete-btn"
                      }}
                      disabled={namedMilestoneCount <= 2}
                      onConfirm={() => {
                        msRenameDraftRef.current = null;
                        onDeleteMilestone(scopePopover.milestoneId);
                        setScopePopover(null);
                      }}
                    >
                      <Button
                        block
                        size="small"
                        danger
                        className="ciq-delete-btn"
                        disabled={namedMilestoneCount <= 2}
                        data-testid="ms-popover-delete"
                      >
                        Delete milestone
                      </Button>
                    </Popconfirm>
                    {namedMilestoneCount <= 2 && (
                      <div
                        style={{
                          fontSize: 11,
                          color: palette.textMuted,
                          marginTop: 6
                        }}
                      >
                        A design package needs at least two milestones
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {addMenu && (
              <div
                ref={addMenuRef}
                data-testid="ms-add-menu"
                className="fixed z-50 overflow-hidden rounded-lg"
                style={{
                  left: addMenu.clientX,
                  top: addMenu.clientY,
                  minWidth: 180,
                  background: palette.surface,
                  border: `1px solid ${palette.divider}`,
                  boxShadow: "0 8px 28px var(--ciq-black-a16)",
                  fontFamily: config.fonts.family
                }}
              >
                <button
                  type="button"
                  data-testid="ms-add-menu-item"
                  className="block w-full cursor-pointer border-0 bg-transparent px-3 py-2 text-left"
                  style={{
                    fontSize: 13,
                    color: palette.text
                  }}
                  onClick={() => {
                    onAddMilestone?.(addMenu.placement);
                    setAddMenu(null);
                  }}
                >
                  Add New Milestone
                </button>
              </div>
            )}
            {/* the rename input needs pointer events, so it sits directly in the
              wrapper rather than inside the pointer-events-none crosshair overlay */}
            {editing && (
              <input
                data-testid={
                  editing.kind === "milestone"
                    ? "ms-name-input"
                    : "bp-name-input"
                }
                // A transient editor that only exists because the user just
                // clicked its trigger, so autofocus here doesn't steal focus
                // from anything the user didn't just interact with.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={editing.value}
                onChange={(e) =>
                  setEditing({ ...editing, value: e.target.value })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  else if (e.key === "Escape") setEditing(null);
                }}
                onBlur={commitRename}
                className="absolute rounded px-1 py-0.5"
                style={{
                  left: editing.box.left,
                  top: editing.box.top,
                  width: Math.max(editing.box.width, 160),
                  fontFamily: config.fonts.family,
                  fontSize: config.fonts.name,
                  color: palette.text,
                  border: `1px solid ${palette.divider}`
                }}
              />
            )}
          </div>
        ) : (
          scrollBlock
        )}

        {undatedTray}

        {/* slot sits above the legend so callers can surface contextual actions
          without breaking out of the timeline's visual container */}
        {footerSlot && <div className="px-3 py-2">{footerSlot}</div>}

        <DependencyLegend config={config} />
      </div>
    );
  }
  return dependencyTimelineBody();
}
